import { cloneElement, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
    listDirectoryIntegrations,
    createDirectoryIntegration,
    updateDirectoryIntegration,
    setDirectoryIntegrationEnabled,
    deleteDirectoryIntegration,
    triggerDirectorySync,
    testDirectoryIntegration,
    getDirectoryAuthUrl,
    getDirectoryIntegration,
    previewDirectoryGroups,
    listDirectorySyncHistory,
    listDirectoryGroups,
} from "../../lib/identityClient.js";
import HoverTooltip from "../HoverTooltip.jsx";
import OverlayDialog from "../OverlayDialog.jsx";
import {
    Fact,
    SourceLogo,
    StatusPill,
    TONE,
    dangerBtnCls,
    ghostBtnCls,
    primaryBtnCls,
    secondaryBtnCls,
} from "../statusCard.jsx";
import {
    AsideSection,
    ConsentWait,
    CopyValue,
    ErrorText,
    Field,
    Instructions,
    MethodOption,
    Panel,
    PermissionList,
    SourcePage,
    Step,
    Steps,
    inputCls,
    linkCls,
} from "../setupSteps.jsx";
import { directoryOauthRedirectUri } from "../../lib/oauthCallback.js";
import { useDirectoryOauthPopup } from "../../lib/useDirectoryOauthPopup.js";
import { listCopilotIntegrations } from "../../lib/copilotClient.js";
import { directoryHoldsTenant } from "../../lib/microsoftTenant.js";
import CopiedMicrosoftAppNote, { COPY_TARGET } from "./CopiedMicrosoftAppNote.jsx";
import {
    ENTRA_DIRECTORY_PERMISSIONS,
    MANAGED_MICROSOFT_PERMISSION_COUNT,
    MANAGED_MICROSOFT_PERMISSION_GROUPS,
    MANAGED_MICROSOFT_PERMISSIONS_NOTE,
} from "./microsoftPermissions.js";
import { hasPermission, useMyPermissions } from "../../lib/permissions.js";
import { PERMISSIONS } from "../../lib/permissionKeys.js";
import { NOTE_CLASS } from "../../lib/ui.js";
import { formatCompact } from "../../lib/renderers.js";
import { timeLabelFormatter, dayLabelFormatter, parseServerTimestamp } from "../../lib/traceUtils.js";
import { buildCredentialPatch } from "./integrationCredentials.js";
import {
    resolveSyncType,
    canSaveScope,
    scopeConfigPayload,
    effectiveScopeKey,
    supportsGroupScope,
} from "./syncScope.js";
import { DIALOG } from "../../lib/analytics/surfaceVocabulary.js";

const PROVIDER_LABEL = {
    ENTRA_ID: "Microsoft Entra ID",
    OKTA: "Okta",
    GOOGLE: "Google Workspace",
};

const PROVIDER_LOGO = {
    ENTRA_ID: {
        viewBox: "0 0 24 24",
        paths: ["M2,3H11V12H2V3M11,22H2V13H11V22M21,3V12H12V3H21M21,22H12V13H21V22Z"],
    },
    OKTA: {
        viewBox: "0 0 24 24",
        paths: ["M12 0C5.389 0 0 5.35 0 12s5.35 12 12 12 12-5.35 12-12S18.611 0 12 0zm0 18c-3.325 0-6-2.675-6-6s2.675-6 6-6 6 2.675 6 6-2.675 6-6 6z"],
    },
    GOOGLE: {
        viewBox: "0 0 24 24",
        paths: ["M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"],
    },
};

const CREDENTIAL_KIND = {
    ENTRA_ID: "Your app registration",
    OKTA: "Your API token",
    GOOGLE: "Your service account",
};

const MANAGED_KIND = {
    ENTRA_ID: "Origin-managed app",
    OKTA: "Origin's read-only app",
};

export function integrationDescription(row) {
    const kind = (row?.managed ? MANAGED_KIND : CREDENTIAL_KIND)[row?.provider];
    const provider = PROVIDER_LABEL[row?.provider] ?? row?.provider ?? "";
    return kind ? `${provider} · ${kind}` : provider;
}

// Maps the UI provider key to the `DirectoryProviderConfig` oneof field the
// gateway returns. The gRPC-gateway serializes responses in camelCase
// (`entraId`, `googleWorkspace`), so reads must use the camelCase key. Used to
// read the stored config out of GetDirectoryIntegration on edit.
const PROVIDER_CONFIG_KEY = { ENTRA_ID: "entraId", OKTA: "okta", GOOGLE: "googleWorkspace" };

// The gateway serializes the config oneof and its fields in camelCase
// (`tenantId`, `orgDomain`, …); the edit form's fields are keyed snake_case
// (matching the proto field names the backend round-trips), so convert on read.
export function camelKeysToSnake(obj) {
    return Object.fromEntries(
        Object.entries(obj).map(([k, v]) => [k.replace(/[A-Z]/g, (m) => `_${m.toLowerCase()}`), v]),
    );
}

// Per-page size for the live-Graph group-scope picker. 200 is the backend's
// per-page cap (`MAX_PAGE_SIZE`); a larger request is clamped to it.
const PREVIEW_PAGE_SIZE = 200;

// The synced-store walk stops as soon as every stored id is named, so the page
// cap only binds on a very large store — and hitting it costs the count line
// for whatever stayed unnamed, not a broken picker. That is what makes a bound
// this low acceptable rather than a silent truncation.
const SYNCED_NAME_LOOKUP_PAGE_SIZE = 200;
const SYNCED_NAME_LOOKUP_MAX_PAGES = 10;

// Consent propagation after a one-click connect takes minutes, not seconds, so
// the ladder spans ~75s before giving up on the 403 and showing guidance.
export const PREVIEW_403_RETRY_DELAYS_MS = [5_000, 10_000, 20_000, 40_000];

// Providers whose connection runs through a browser consent redirect. Okta is not
// one: an API token is pasted, and a service app signs an assertion.
const OAUTH_PROVIDERS = new Set(["ENTRA_ID"]);

// No shape flag goes on the wire — the backend reads managed Entra off an empty
// config and managed Okta off the absence of an `api_token` — so the entry point the
// admin picks decides only which fields get collected.
const ENTRA_ALREADY_CONNECTED = "This Copilot connection's Microsoft tenant already has an Entra directory connection.";

const SETUP_CHOICES = [
    {
        key: "entra",
        provider: "ENTRA_ID",
        managed: true,
        title: "Microsoft Entra ID",
        description: "Sign in with Microsoft and approve Origin's app. Nothing to create or rotate.",
    },
    {
        key: "okta",
        provider: "OKTA",
        managed: true,
        title: "Okta",
        description: "You create a read-only app in Okta, and Origin signs in to it with its own key.",
    },
    {
        key: "google",
        provider: "GOOGLE",
        managed: false,
        title: "Google Workspace",
        description: "You create a service account in Google Cloud and allow it to read your directory.",
    },
];

const MANUAL_CHOICES = [
    {
        key: "entra-byo",
        provider: "ENTRA_ID",
        managed: false,
        title: "Microsoft Entra ID with your own app",
        description: "You register the app in Entra and replace its client secret before it expires.",
    },
    {
        key: "okta-token",
        provider: "OKTA",
        managed: false,
        title: "Okta with an API token",
        description: "You paste a token made by a read-only Okta administrator.",
    },
];

const EDIT_AT_RISK = {
    ENTRA_ID: "Credentials are expiring or being rejected. Re-authorize below before syncs start failing.",
    OKTA: "Credentials are being rejected. Check that the API token is still valid, or that the app is still active in Okta.",
    GOOGLE: "Credentials are being rejected. Paste a new service account key below before syncs start failing.",
};

export function defaultName(directories, provider) {
    const label = PROVIDER_LABEL[provider];
    if (!label) return "";
    const taken = new Set(directories.filter((d) => d.provider === provider).map((d) => d.displayName));
    if (!taken.has(label)) return label;
    let n = 2;
    while (taken.has(`${label} ${n}`)) n += 1;
    return `${label} ${n}`;
}

function integrationKindKey(provider, managed) {
    return [...SETUP_CHOICES, ...MANUAL_CHOICES].find((c) => c.provider === provider && c.managed === managed)?.key ?? null;
}

const SETUP_ASIDE = {
    entra: { title: "Nothing to maintain", text: "Origin owns the app and its credentials. You can remove its consent in Entra at any time." },
    "entra-byo": { title: "Client secret", text: "Stored encrypted. Replace it here before it expires in Entra." },
    okta: { title: "No stored secret", text: "Origin signs in with its own key. The app stays in your Okta." },
    "okta-token": { title: "API token", text: "Stored encrypted. Replace it here if Okta revokes it." },
    google: { title: "Service account key", text: "Stored encrypted. Deleting this connection does not remove the key or the delegation grant." },
};

const PROVIDER_FIELDS = {
    ENTRA_ID: [
        { key: "tenant_id", label: "Directory (tenant) ID", type: "text", required: true,
          placeholder: "00000000-0000-0000-0000-000000000000",
          hint: "On the app's Overview page." },
        { key: "client_id", label: "Application (client) ID", type: "text", required: true,
          placeholder: "00000000-0000-0000-0000-000000000000",
          hint: "On the same Overview page." },
    ],
    // `client_id` is deliberately absent: the customer's service app does not exist
    // yet at create, so the setup panel collects it once it does.
    OKTA: [
        { key: "org_domain", label: "Okta domain", type: "text", required: true,
          placeholder: "acme.okta.com",
          hint: "The address you sign in to Okta at." },
    ],
    GOOGLE: [
        { key: "customer_id", label: "Customer ID", type: "text", required: true, placeholder: "C0xxxxxxx",
          hint: "In admin.google.com under Account → Account settings. It starts with C." },
        { key: "admin_email", label: "Admin email", type: "text", required: true, placeholder: "admin@acme.com",
          hint: "A super admin. Origin reads the directory as this person." },
    ],
};

const PROVIDER_CREDENTIALS = {
    ENTRA_ID: [
        { key: "client_secret", label: "Client secret", type: "password", required: true,
          placeholder: "The secret's Value, not its ID" },
    ],
    // This key IS the shape signal — no `api_token` reaches the backend as managed.
    OKTA: [
        { key: "api_token", label: "API token", type: "password", required: true,
          placeholder: "Paste the token",
          hint: "An OAuth client ID and secret will not work here." },
    ],
    GOOGLE: [
        { key: "service_account_json", label: "Service account key (JSON)", type: "textarea", required: true,
          placeholder: "Paste the whole key file" },
    ],
};

// Every Okta org host the backend accepts, EMEA included.
const OKTA_DOMAIN_SUFFIXES = [".okta.com", ".oktapreview.com", ".okta-emea.com"];

/**
 * Mirror of the backend's `normalize_okta_domain` plus its domain validator, minus
 * the normalizing: a bare host and a full URL are both fine, because the backend
 * prefixes `https://` either way. Rejects only what a save would.
 */
export function validateOktaDomain(value) {
    let host = value.trim();
    if (host.includes("://")) {
        let url;
        try { url = new URL(host); }
        catch { return "Not a valid URL. `acme.okta.com` on its own works too."; }
        if (url.protocol !== "https:") return "Must be https.";
        if (url.username || url.password) return "Remove the credentials before the host.";
        if (url.port) return "Remove the port.";
        if (url.search || url.hash) return "Remove everything after the host.";
        if (url.pathname !== "" && url.pathname !== "/") return "Remove the path. Enter the org host on its own.";
        host = url.hostname;
    }
    host = host.replace(/\/+$/, "").toLowerCase();
    if (!host) return "required";
    if (host.includes("/")) return "Remove the path. Enter the org host on its own.";
    const suffix = OKTA_DOMAIN_SUFFIXES.find((s) => host.endsWith(s) && host.length > s.length);
    if (!suffix) return `Must be an Okta org host ending in ${OKTA_DOMAIN_SUFFIXES.join(", ")}.`;
    // Passes every check above, then 404s at the token endpoint (see `OktaDomainError::AdminHost`).
    if (host.slice(0, -suffix.length).endsWith("-admin")) {
        return `That is the admin console host. Use ${host.replace("-admin" + suffix, suffix)} instead, which is the Okta domain shown under your profile menu.`;
    }
    return "";
}

export function validateField(provider, field, value) {
    if (!value) return field.required ? "required" : "";
    if (provider === "GOOGLE") {
        if (field.key === "customer_id" && !/^[A-Za-z0-9_-]{4,32}$/.test(value)) {
            return "Customer ID looks malformed (alphanumeric token from admin.google.com → Account)";
        }
        if (field.key === "admin_email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
            return "Admin Email must be a valid email address";
        }
        if (field.key === "service_account_json") {
            let parsed;
            try { parsed = JSON.parse(value); }
            catch { return "Invalid JSON. Paste the complete key from GCP IAM → Service Accounts → Keys."; }
            if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return "Must be a JSON object";
            if (parsed.type !== "service_account") {
                return parsed.type
                    ? `\`type\`="${parsed.type}"; expected "service_account"`
                    : "Missing `type` field. Paste the unmodified file.";
            }
            for (const k of ["client_email", "private_key", "token_uri", "project_id"]) {
                if (typeof parsed[k] !== "string" || !parsed[k]) return `Missing required field: ${k}`;
            }
            if (!parsed.private_key.startsWith("-----BEGIN PRIVATE KEY-----")) {
                return "The private_key value is not a PEM key. It should begin with -----BEGIN PRIVATE KEY-----. Paste the JSON exactly as Google downloaded it.";
            }
            if (!parsed.private_key.includes("-----END PRIVATE KEY-----")) {
                return "`private_key` is truncated (no END marker)";
            }
            if (!parsed.client_email.endsWith(".iam.gserviceaccount.com")) {
                return "`client_email` is not a service-account address";
            }
        }
    }
    if (provider === "OKTA" && field.key === "org_domain") return validateOktaDomain(value);
    if (provider === "ENTRA_ID" && (field.key === "tenant_id" || field.key === "client_id")) {
        if (!/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(value)) {
            return `${field.label} must be a UUID (from the Azure portal app registration)`;
        }
    }
    return "";
}

/**
 * Delay before the next in-flight poll, given how long this run has been polled.
 *
 * A sync has no wall-clock cap — the backend bounds it by silence, with a
 * multi-hour ceiling behind that — so the tail tier is what keeps a long run
 * from costing ~12 list calls a minute for hours.
 */
export function syncPollDelayMs(elapsedMs) {
    if (elapsedMs < 30_000) return 5_000;
    if (elapsedMs < 120_000) return 15_000;
    if (elapsedMs < 600_000) return 30_000;
    return 60_000;
}

// The gateway's human text. `.message` is the developer form
// (`POST /path 400: {json}`) and leaks request shape at the user.
//
// isGraphPermissionError and isForbiddenError must keep reading `.message` —
// they match the HTTP status and Graph error code, which `serverMessage` drops.
export function humanError(err, fallback) {
    return err?.serverMessage || err?.message || fallback;
}

export function flattenIntegration(raw) {
    // The three advance independently, so this is a max and not a fallback chain:
    // picking the first one present pins the column to whichever kind ran first.
    const last = [raw.lastFullSyncAt, raw.lastIncrementalSyncAt, raw.lastScopedSyncAt]
        .map(parseServerTimestamp)
        .filter(Number.isFinite)
        .reduce((a, b) => Math.max(a, b), -Infinity);
    const lastDate = Number.isFinite(last) ? new Date(last) : null;
    return {
        id:             raw.id,
        displayName:    raw.displayName ?? "",
        provider:       raw.provider ?? "",
        status:         raw.status ?? "pending",
        // Carried onto the row so a delete confirmation can say what survives it.
        managed:        !!raw.managed,
        // Effective sync scope (whole_tenant | scoped | not_configured),
        // normalized in identityClient. `null` on a pre-field backend → no badge.
        syncScope:      raw.syncScope ?? null,
        isPrimary:      !!raw.isPrimary,
        boundDirectoryId: raw.boundDirectoryId ?? raw.bound_directory_id ?? "",
        lastSyncDate:   Number.isFinite(lastDate?.getTime?.()) ? lastDate : null,
        lastSyncError:  raw.lastSyncError ?? "",
    };
}

// Replace snake_case with a space and Title-Case the words so
// `at_risk` reads as `At risk` in the cell.
function prettyStatus(s) {
    if (!s) return "—";
    const parts = String(s).split("_");
    return parts.map((p, i) => i === 0 ? p.charAt(0).toUpperCase() + p.slice(1) : p).join(" ");
}

// Only Entra has a Re-authorize control (`OAUTH_PROVIDERS`), so sending the other
// two there points at a button they do not have. The server flags `at_risk` on a
// 401 from any provider, Okta and Google included.
const AT_RISK_REMEDY = {
    ENTRA_ID: "Use Edit → Re-authorize before syncs start failing.",
    OKTA: "Check under Edit that the API token is still valid, or that the service app is still active in Okta.",
    GOOGLE: "Update the service-account key under Edit before syncs start failing.",
};

export function statusHelp(status, provider) {
    if (status !== "at_risk") return undefined;
    const remedy = AT_RISK_REMEDY[provider];
    return remedy
        ? `Credentials are expiring or being rejected. ${remedy}`
        : "Credentials are expiring or being rejected. Check the integration under Edit before syncs start failing.";
}

const STATUS_PILL = {
    connected: { tone: TONE.OK, label: "Connected" },
    healthy:   { tone: TONE.OK, label: "Connected" },
    syncing:   { tone: TONE.OK, label: "Syncing", pulse: true },
    pending:   { tone: TONE.SETUP, label: "Not connected" },
    at_risk:   { tone: TONE.WAITING, label: "Credentials at risk" },
    error:     { tone: TONE.ATTENTION, label: "Sync failing" },
    disabled:  { tone: TONE.SETUP, label: "Disabled" },
    deleting:  { tone: TONE.ATTENTION, label: "Deleting", pulse: true },
};

export function statusPill(status) {
    return STATUS_PILL[status] ?? { tone: TONE.SETUP, label: prettyStatus(status) };
}

const CREDENTIAL_FACT = {
    connected: { text: "Valid", tone: null },
    healthy:   { text: "Valid", tone: null },
    at_risk:   { text: "Expiring or rejected", tone: "warning" },
    disabled:  { text: "Not checked", tone: "muted" },
};

export function credentialFact(status) {
    if (status === "pending" || status === "deleting") return null;
    return CREDENTIAL_FACT[status] ?? { text: "—", tone: "muted" };
}

const SYNC_SCOPE_LABEL = {
    whole_tenant:   "Whole tenant",
    scoped:         "Selected groups",
    not_configured: "Not configured",
};

function lastSyncLabel(date) {
    if (!(date instanceof Date)) return "Never";
    return `${dayLabelFormatter.format(date)} ${timeLabelFormatter.format(date)}`;
}

function PrimaryBadge() {
    return (
        <HoverTooltip
            label="When one person is in two connected directories, Origin uses their account from this one."
            placement="top"
            wide
        >
            <span
                tabIndex={0}
                className="inline-flex flex-none items-center rounded-[2px] border border-[color:var(--line-strong)] px-[6px] py-[1px] text-[10px] font-normal uppercase tracking-[0.06em] leading-[1.3] text-[color:var(--muted)]"
            >
                Primary
            </span>
        </HoverTooltip>
    );
}

// ── Sync history (per-integration drill-down) ────────────────────

export function formatDuration(ms) {
    if (ms == null) return "—";
    const s = ms / 1000;
    if (s < 60) return `${Math.round(s)}s`;
    const m = Math.floor(s / 60);
    const r = Math.round(s % 60);
    return `${m}m ${r}s`;
}

// ── Re-authorize + abandon-cleanup decisions (extracted so the state machine is
//    unit-tested directly; the component wires them into render/effects) ──

// Re-authorize re-runs OAuth/admin-consent on an EXISTING integration. Shown only
// on the edit form, only for OAuth providers (Entra/Okta — Google uses a
// service account, no consent popup), and only with write permission.
export function reauthorizeVisible(isEdit, isOauth, canWrite) {
    return !!(isEdit && isOauth && canWrite);
}

// Whether the speculative row created for an in-flight OAuth connect is
// auto-deleted on abandonment (modal close / navigate-away / consent failure).
// Only a MANAGED row is: Origin owns the app and the row holds no admin input. A
// BYO row keeps the admin's entered app config and persists (resumable via
// Re-authorize, or manually deletable). `cleanup` is `{ id, managed } | null`.
export function shouldCleanupAbandoned(cleanup) {
    return !!cleanup?.managed;
}

// The waiting-banner Cancel deletes the speculative row only for a fresh connect
// (create mode). Cancelling a Re-authorize (edit mode) must never delete the
// existing integration.
export function cancelDeletesSpeculativeRow(isEdit) {
    return !isEdit;
}

// Keys match `normalizeSyncStatus` output in identityClient.js. `partial` is
// amber rather than red: the run wrote its results and has real counts, it just
// hit errors on the way — which is also why it skipped the tombstone pass.
function SyncStatusBadge({ status }) {
    const palette =
        status === "running"   ? "border-[color:var(--blue,#5eb1ff)] text-[color:var(--blue,#5eb1ff)]" :
        status === "completed" ? "border-[color:var(--green)] text-[color:var(--green)]" :
        status === "failed"    ? "border-red-400 text-red-400" :
        status === "partial"   ? "border-amber-400/50 text-amber-400" :
        "border-[color:var(--line)] text-[color:var(--muted)]";
    return (
        <span className={`inline-flex items-center rounded-[2px] border px-[5px] py-[1px] text-[9px] uppercase tracking-[0.08em] font-medium ${palette}`}>
            {status || "—"}
        </span>
    );
}

function SyncHistoryView({ integrationId }) {
    const [entries, setEntries] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    useEffect(() => {
        if (!integrationId) return undefined;
        let cancelled = false;
        setLoading(true);
        setError(null);
        listDirectorySyncHistory(integrationId, { pageSize: 50 })
            .then((res) => { if (!cancelled) setEntries(res?.entries ?? res?.history ?? []); })
            .catch((err) => { if (!cancelled) { setError(humanError(err, "Failed to load sync history")); setEntries([]); } })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [integrationId]);

    if (loading) return <div className={`${NOTE_CLASS} text-[12px]`}>Loading…</div>;
    if (error) return <div className="text-[12px] text-red-400">{error}</div>;
    if (!entries?.length) return <div className={`${NOTE_CLASS} text-[12px]`}>No sync runs recorded yet.</div>;

    return (
        <div className="grid gap-[6px] max-h-[60vh] overflow-y-auto">
            {entries.map((e, i) => {
                const started = e.startedAt ?? e.started_at;
                const duration = e.durationMs ?? e.duration_ms;
                const users = e.usersSynced ?? e.users_synced;
                const groups = e.groupsSynced ?? e.groups_synced;
                const devices = e.devicesSynced ?? e.devices_synced;
                const errCount = e.errorCount ?? e.error_count;
                // Proto field is `last_error` (wire `lastError`), not
                // `error_message` — reading the wrong key is why a run shows an
                // error *count* but no message.
                const errMsg = e.lastError ?? e.last_error;
                const startedDate = started ? new Date(parseServerTimestamp(started)) : null;
                return (
                    <div key={e.id ?? i} className="grid gap-[4px] border border-[color:var(--line)] px-[10px] py-[6px]">
                        <div className="flex items-center justify-between gap-[8px]">
                            <span className="flex items-center gap-[8px] text-[12px] text-[color:var(--ink)]">
                                <SyncStatusBadge status={e.status} />
                                <span className="font-medium">{prettyStatus(e.syncType ?? e.sync_type ?? "sync")}</span>
                                {startedDate ? (
                                    <span className={`${NOTE_CLASS} text-[11px]`}>
                                        {dayLabelFormatter.format(startedDate)} {timeLabelFormatter.format(startedDate)}
                                    </span>
                                ) : null}
                            </span>
                            <span className={`${NOTE_CLASS} text-[11px]`}>{formatDuration(duration)}</span>
                        </div>
                        <div className="flex flex-wrap items-center gap-[10px] text-[11px] text-[color:var(--muted)]">
                            {users != null  ? <span><strong className="text-[color:var(--ink)] font-medium">{formatCompact(users)}</strong> users</span> : null}
                            {groups != null ? <span><strong className="text-[color:var(--ink)] font-medium">{formatCompact(groups)}</strong> groups</span> : null}
                            {devices != null ? <span><strong className="text-[color:var(--ink)] font-medium">{formatCompact(devices)}</strong> devices</span> : null}
                            {errCount ? <span className="text-red-400"><strong className="font-medium">{errCount}</strong> errors</span> : null}
                        </div>
                        {errMsg ? <div className="text-[11px] text-red-400 break-all">{errMsg}</div> : null}
                    </div>
                );
            })}
        </div>
    );
}

// Sync-scope picker. Every provider offers a "Sync full tenant" toggle (default
// off); Entra additionally lists the tenant's groups live from Microsoft Graph
// (no prior sync needed) for a scoped selection. Whole-tenant vs. scoped is
// stored on the integration's config (`sync_full_tenant` + `synced_group_ids`),
// not a tenant entitlement — enabling full-tenant clears any group selection on
// save (whole-tenant wins). Until the admin enables full-tenant or (Entra)
// picks at least one group, the integration is "not configured" and syncs
// nothing. Scoped group sync is Entra-only for now; other providers show only
// the toggle.
// Microsoft Graph denies the live group preview with this stable error code
// when the app registration lacks a directory-read permission — including the
// window right after a one-click connect where admin consent hasn't propagated
// yet. The backend maps that Graph 403 to gRPC FailedPrecondition, which the
// gateway serializes as HTTP 400 (NOT 403), so the HTTP status is no help;
// detect the case by the Graph error code echoed verbatim into the body.
function isGraphPermissionError(err) {
    return /Authorization_RequestDenied/.test(String(err?.message ?? err ?? ""));
}

/**
 * Google needs a nesting caveat Entra does not: a scoped run drops any sub-group
 * edge pointing outside the allowlist (`google/scoped.rs`).
 */
const GROUP_PICKER_INTRO = {
    ENTRA_ID: "Choose the Entra groups to sync. Members of selected groups and nested groups are monitored. After the next successful full sync, people outside those groups leave the access directory, so their activity is hidden from every restricted viewer.",
    OKTA: "Choose the groups to sync. Members of the selected groups are monitored; other users are removed on the next sync.",
    GOOGLE: "Choose the Google groups to sync. Members of the selected groups are monitored. If a selected group contains another group, select that one too, or its members are left out. Other users are removed on the next sync.",
    DEFAULT: "Choose the groups to sync. Members of the selected groups are monitored; other users are removed on the next sync.",
};

export function groupsForbiddenHelp(provider, managed) {
    if (provider === "OKTA") {
        return "Origin can’t read groups from this org. Check that the app has the okta.groups.read scope and an admin role assigned. Without the role, the scope alone grants no access.";
    }
    if (provider === "GOOGLE") {
        return "Origin can’t read groups from this Workspace. Check that the domain-wide delegation grant for this client ID includes admin.directory.group.readonly, and that the Admin SDK API is enabled in the service account’s Google Cloud project.";
    }
    if (managed) {
        return "If you’ve just connected Microsoft Entra, it can take Microsoft a few minutes to finish granting Origin access. Wait a moment, then try again.";
    }
    return "Origin doesn’t have permission to read groups from this directory. Confirm the app registration has the required Microsoft Graph directory permissions and that admin consent has been granted, then reopen this dialog.";
}

export function GroupScopeView({ integrationId, provider, onSaved }) {
    const scopable = supportsGroupScope(provider);
    const configKey = PROVIDER_CONFIG_KEY[provider];

    const [configLoading, setConfigLoading] = useState(true);
    const [configError, setConfigError] = useState(null);
    const [providerCfg, setProviderCfg] = useState(null);
    const [syncFull, setSyncFull] = useState(false);
    const [selected, setSelected] = useState(() => new Set());
    // Only the selection as *loaded* needs naming from the synced store; anything
    // ticked afterwards came off the Graph list with its name attached.
    const [storedSelection, setStoredSelection] = useState(null);
    // Separates "we don't know yet" from "this group has never synced" — the
    // first is a wait, the second a fact about the directory.
    const [namesResolving, setNamesResolving] = useState(false);
    const [showUnnamedIds, setShowUnnamedIds] = useState(false);
    // Effective scope at load, so a no-op save doesn't kick off a re-sync.
    const loadedScopeKeyRef = useRef(null);
    // Managed (one-click) vs BYO governs how a group-preview 403 is handled:
    // a managed integration's permissions propagate on their own, so we retry;
    // a BYO 403 is likely a real misconfiguration the admin must fix.
    const [managed, setManaged] = useState(false);

    const [search, setSearch] = useState("");
    const [groups, setGroups] = useState([]);
    // id → displayName, fed by the live-Graph pages and by the synced store
    // below. Graph pages 200 at a time, so a large tenant's stored selection is
    // mostly not on page one and would otherwise be unreviewable.
    const [groupNames, setGroupNames] = useState(() => new Map());
    const [groupsToken, setGroupsToken] = useState(null);
    const [groupsLoading, setGroupsLoading] = useState(false);
    const [groupsError, setGroupsError] = useState(null);
    // A group preview returned 403 — show permission guidance instead of the
    // raw error. `retrying` covers the delay before the one automatic retry.
    const [forbidden, setForbidden] = useState(false);
    const [retrying, setRetrying] = useState(false);

    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);

    const mountedRef = useRef(true);
    const retryTimerRef = useRef(null);
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
        };
    }, []);

    // Load the stored provider config + current scope once. Reads camelCase —
    // the gateway serializes the wrapper as `{ entraId|okta|googleWorkspace,
    // syncFullTenant }`.
    useEffect(() => {
        let cancelled = false;
        setConfigLoading(true);
        setConfigError(null);
        getDirectoryIntegration(integrationId)
            .then((detail) => {
                if (cancelled) return;
                const cfg = detail?.config?.[configKey] ?? {};
                setProviderCfg(cfg);
                setManaged(!!detail?.integration?.managed);
                setSyncFull(!!detail?.config?.syncFullTenant);
                const storedIds = cfg.syncedGroupIds ?? [];
                setSelected(new Set(storedIds));
                setStoredSelection(storedIds);
                loadedScopeKeyRef.current = effectiveScopeKey({
                    syncFull: !!detail?.config?.syncFullTenant,
                    scopable,
                    selected: storedIds,
                });
            })
            .catch((e) => {
                if (!cancelled) setConfigError(humanError(e, "Failed to load integration"));
            })
            .finally(() => {
                if (!cancelled) setConfigLoading(false);
            });
        return () => { cancelled = true; };
    }, [integrationId, configKey]);

    // Server-side search + cursor pagination (mirrors DirectoryBrowserTab): a
    // no-token fetch resets the list, a token fetch ("Load more") appends.
    const fetchGroups = useCallback(async (token, { retryAttempt = null } = {}) => {
        setGroupsLoading(true);
        if (!token) {
            setGroupsError(null);
            setForbidden(false);
            // A fresh reset fetch (open, search, retry, or Try again) supersedes
            // any pending auto-retry — cancel it so a stale one can't fire.
            if (retryTimerRef.current) { clearTimeout(retryTimerRef.current); retryTimerRef.current = null; }
        }
        try {
            const res = await previewDirectoryGroups(integrationId, {
                search: search || undefined,
                pageSize: PREVIEW_PAGE_SIZE,
                pageToken: token || undefined,
            });
            if (!mountedRef.current) return;
            const list = res?.groups ?? [];
            // Never re-sort the accumulated pages: that reshuffles the list on
            // every "Load more", moving rows out from under the cursor.
            setGroups((prev) => (token ? [...prev, ...list] : list));
            // Retained so a selection made on an earlier page stays nameable.
            setGroupNames((prev) => {
                const next = new Map(prev);
                for (const g of list) next.set(g.id, g.displayName || "");
                return next;
            });
            setGroupsToken(res?.nextPageToken || null);
            setRetrying(false);
        } catch (err) {
            if (!mountedRef.current) return;
            // Graph denied the initial (reset) load for lack of a directory
            // permission. For a managed (one-click) integration that's almost
            // always still-propagating admin consent, so back off and retry a few
            // times before surfacing manual guidance. For BYO it's far more
            // likely a genuinely missing permission/consent, so skip the retries
            // and show fix-your-permissions guidance immediately.
            // A bare 403 counts too: only Entra emits `Authorization_RequestDenied`,
            // so keying on that alone leaves Okta and Google no guidance at all.
            if (!token && (isGraphPermissionError(err) || isForbiddenError(err))) {
                // Retry only a Graph denial, which is usually consent still
                // propagating. Any other 403 is a real missing permission.
                const delay = managed && retryAttempt !== null && isGraphPermissionError(err)
                    ? PREVIEW_403_RETRY_DELAYS_MS[retryAttempt]
                    : undefined;
                if (delay !== undefined) {
                    setRetrying(true);
                    retryTimerRef.current = setTimeout(() => {
                        if (mountedRef.current) {
                            fetchGroups(undefined, { retryAttempt: retryAttempt + 1 });
                        }
                    }, delay);
                } else {
                    setRetrying(false);
                    setForbidden(true);
                }
                return;
            }
            setGroupsError(humanError(err, "Failed to load groups"));
        } finally {
            if (mountedRef.current) setGroupsLoading(false);
        }
    }, [integrationId, search, managed]);

    // Only Entra has a group picker — don't hit Graph for other providers.
    // Debounce the reset fetch: this hits live, rate-limited Graph, so don't
    // fire a request per keystroke (the synced-store browser tab can afford to).
    // Wait for the config load so `managed` is known before a 403 can arrive —
    // the retry-vs-guidance branch depends on it.
    useEffect(() => {
        if (!scopable || configLoading) return;
        const t = setTimeout(() => { fetchGroups(undefined, { retryAttempt: 0 }); }, 300);
        return () => clearTimeout(t);
    }, [scopable, configLoading, fetchGroups]);

    // Name the stored selection from our own synced store rather than Graph —
    // for a scoped integration it holds exactly the groups being synced, so this
    // reaches a selection sitting far past Graph page one at no Graph cost.
    useEffect(() => {
        if (!scopable || !storedSelection?.length) return;
        let cancelled = false;
        setNamesResolving(true);

        (async () => {
            // Ordered by display_name, so selected ids are scattered across
            // pages rather than sitting at the front — walk until all are named.
            const wanted = new Set(storedSelection);
            let pageToken = "";
            for (let page = 0; page < SYNCED_NAME_LOOKUP_MAX_PAGES; page += 1) {
                const res = await listDirectoryGroups({
                    provider,
                    integration_id: integrationId,
                    page_size: SYNCED_NAME_LOOKUP_PAGE_SIZE,
                    page_token: pageToken || undefined,
                });
                if (cancelled) return;

                const found = new Map();
                for (const g of res?.groups ?? []) {
                    if (g.sourceId && wanted.delete(g.sourceId)) {
                        found.set(g.sourceId, g.displayName || "");
                    }
                }
                if (found.size > 0) {
                    setGroupNames((prev) => {
                        const next = new Map(prev);
                        for (const [id, name] of found) {
                            // A live Graph page wins: the synced store lags a
                            // rename by up to one sync interval.
                            if (!next.has(id)) next.set(id, name);
                        }
                        return next;
                    });
                }

                pageToken = res?.nextPageToken ?? "";
                if (!pageToken || wanted.size === 0) return;
            }
        })()
            // Best-effort: this costs chip labels, never the picker itself.
            .catch(() => {})
            .finally(() => {
                if (!cancelled) setNamesResolving(false);
            });

        return () => { cancelled = true; };
    }, [scopable, provider, integrationId, storedSelection]);

    const toggle = useCallback((id) => {
        setSelected((cur) => {
            const next = new Set(cur);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const canSave = canSaveScope({ syncFull, scopable, selectedCount: selected.size });

    // Only named groups get a chip; the rest collapse to a single count line.
    // Chipping them too gives a row of identical placeholder pills that offer a
    // remove button for something the admin can't identify, and that relabel and
    // re-sort under the cursor as names stream in.
    const { namedChips, unnamedIds } = useMemo(() => {
        const named = [];
        const unnamed = [];
        for (const id of selected) {
            const name = groupNames.get(id);
            if (name) named.push({ id, label: name });
            else unnamed.push(id);
        }
        named.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
        return { namedChips: named, unnamedIds: unnamed };
    }, [selected, groupNames]);

    const onSave = useCallback(async () => {
        if (!providerCfg) return;
        setSaving(true);
        setSaveError(null);
        try {
            const config = scopeConfigPayload({ providerCfg, scopable, syncFull, selected });
            await updateDirectoryIntegration(integrationId, {
                provider,
                config,
                syncFullTenant: syncFull,
            });
            // An allowlist not enforced until the next scheduled run is a
            // security decision that silently hasn't taken effect.
            const scopeChanged =
                effectiveScopeKey({ syncFull, scopable, selected }) !== loadedScopeKeyRef.current;
            onSaved?.({ scopeChanged });
        } catch (e) {
            setSaveError(humanError(e, "Failed to save sync scope"));
            setSaving(false);
        }
    }, [integrationId, provider, scopable, providerCfg, syncFull, selected, onSaved]);

    if (configLoading) return <div className={`${NOTE_CLASS} py-[12px]`}>Loading…</div>;
    if (configError && !providerCfg) {
        return <div className="text-red-400 text-[12px] py-[12px]">{configError}</div>;
    }

    return (
        <div className="grid gap-[12px]">
            <label className="flex items-start gap-[8px] cursor-pointer">
                <input
                    type="checkbox"
                    checked={syncFull}
                    onChange={(e) => setSyncFull(e.target.checked)}
                    className="mt-[2px]"
                />
                <span className="grid gap-[2px]">
                    <span className="text-[12px] text-[color:var(--ink)]">Sync full tenant</span>
                    <span className={`${NOTE_CLASS} text-[11px] leading-[1.3]`}>
                        Sync the entire directory.{" "}
                        {scopable
                            ? "Turn off to sync only selected groups."
                            : "Scoped group sync isn’t available for this provider yet."}
                    </span>
                </span>
            </label>

            {scopable ? (
                <div
                    className={`grid gap-[10px] ${syncFull ? "opacity-40 pointer-events-none" : ""}`}
                    aria-disabled={syncFull}
                >
                {forbidden ? (
                    <div className="grid gap-[8px] border border-[color:var(--line)] p-[12px]" role="alert">
                        <span className="text-[12px] text-[color:var(--ink)]">
                            {`We couldn’t load your ${PROVIDER_LABEL[provider] ?? "directory"} groups${managed && provider === "ENTRA_ID" ? " yet" : ""}.`}
                        </span>
                        <span className={`${NOTE_CLASS} text-[11px] leading-[1.4]`}>
                            {groupsForbiddenHelp(provider, managed)}
                        </span>
                        {managed ? (
                            <button
                                type="button"
                                onClick={() => fetchGroups(undefined, { retryAttempt: 0 })}
                                disabled={syncFull}
                                className={`${secondaryBtnCls} justify-self-start`}
                            >
                                Try again
                            </button>
                        ) : null}
                    </div>
                ) : (
                <>
                    <p className="text-[12px] leading-[1.3] text-[color:var(--muted)]">
                        {GROUP_PICKER_INTRO[provider] ?? GROUP_PICKER_INTRO.DEFAULT}
                    </p>
                    <input
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search groups by name…"
                        className={inputCls}
                        disabled={syncFull}
                    />
                    {groupsError ? (
                        <span className="text-red-400 text-[11px]">{groupsError}</span>
                    ) : null}
                    {selected.size > 0 ? (
                        <div
                            className="grid gap-[6px] border border-[color:var(--line)] p-[8px]"
                            aria-busy={namesResolving}
                        >
                            <span className={`${NOTE_CLASS} text-[10px] uppercase tracking-[0.08em]`}>
                                Selected · {selected.size}
                            </span>
                            {namedChips.length > 0 ? (
                                <div className="flex flex-wrap gap-[6px]">
                                    {namedChips.map(({ id, label }) => (
                                        <button
                                            key={id}
                                            type="button"
                                            onClick={() => toggle(id)}
                                            disabled={syncFull}
                                            title={`Remove ${label}`}
                                            className="flex items-center gap-[6px] max-w-[240px] border border-[color:var(--line)] rounded-[2px] px-[6px] py-[2px] text-[11px] text-[color:var(--ink)] hover:border-red-400 hover:text-red-300 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                                        >
                                            <span className="truncate">{label}</span>
                                            <span aria-hidden="true">×</span>
                                            <span className="sr-only">Remove {label}</span>
                                        </button>
                                    ))}
                                </div>
                            ) : null}
                            {unnamedIds.length > 0 ? (
                                namesResolving ? (
                                    <span className={`${NOTE_CLASS} text-[11px]`}>
                                        Looking up {unnamedIds.length} more {unnamedIds.length === 1 ? "name" : "names"}…
                                    </span>
                                ) : (
                                    <div className="grid gap-[6px]">
                                        <span className={`${NOTE_CLASS} text-[11px]`}>
                                            No name on file for{" "}
                                            {unnamedIds.length === 1 ? "one of these groups" : `${unnamedIds.length} of these groups`}.{" "}
                                            If {unnamedIds.length === 1 ? "it is" : "they are"} still unnamed after a
                                            sync, {unnamedIds.length === 1 ? "it" : "they"} may no longer exist in{" "}
                                            {PROVIDER_LABEL[provider] ?? "the directory"}.{" "}
                                            <button
                                                type="button"
                                                onClick={() => setShowUnnamedIds((v) => !v)}
                                                className="underline underline-offset-2 hover:text-[color:var(--ink)] cursor-pointer"
                                            >
                                                {showUnnamedIds ? "Hide IDs" : "Show IDs"}
                                            </button>
                                        </span>
                                        {showUnnamedIds ? (
                                            // Opt-in: removing a group you can't
                                            // identify is the wrong default, but
                                            // the escape hatch has to exist.
                                            <div className="flex flex-wrap gap-[6px]">
                                                {unnamedIds.map((id) => (
                                                    <button
                                                        key={id}
                                                        type="button"
                                                        onClick={() => toggle(id)}
                                                        disabled={syncFull}
                                                        title={`Remove group ${id}`}
                                                        className={`flex items-center gap-[6px] max-w-[240px] border border-[color:var(--line)] rounded-[2px] px-[6px] py-[2px] font-mono text-[10px] ${NOTE_CLASS} hover:border-red-400 hover:text-red-300 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed`}
                                                    >
                                                        <span className="truncate">{id}</span>
                                                        <span aria-hidden="true">×</span>
                                                        <span className="sr-only">Remove group {id}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        ) : null}
                                    </div>
                                )
                            ) : null}
                        </div>
                    ) : null}
                    <div
                        className="grid gap-[2px] max-h-[280px] overflow-auto border border-[color:var(--line)] p-[6px]"
                        aria-busy={groupsLoading || retrying}
                    >
                        {groups.length === 0 ? (
                            <span className={`${NOTE_CLASS} text-[11px] p-[6px]`}>
                                {retrying
                                    ? `Waiting for ${PROVIDER_LABEL[provider] ?? "the directory"} to grant access. Retrying…`
                                    : groupsLoading
                                      ? "Loading groups…"
                                      : search
                                        ? "No groups match."
                                        : "No groups found."}
                            </span>
                        ) : (
                            groups.map((g) => (
                                <label
                                    key={g.id}
                                    className="flex items-center gap-[8px] px-[6px] py-[4px] text-[12px] text-[color:var(--ink)] hover:bg-[color:var(--control-bg)] cursor-pointer"
                                >
                                    <input
                                        type="checkbox"
                                        checked={selected.has(g.id)}
                                        onChange={() => toggle(g.id)}
                                        disabled={syncFull}
                                    />
                                    <span className="truncate">{g.displayName || "(unnamed group)"}</span>
                                </label>
                            ))
                        )}
                        {groupsToken ? (
                            <button
                                type="button"
                                onClick={() => fetchGroups(groupsToken)}
                                disabled={groupsLoading || syncFull}
                                className="mt-[2px] py-[4px] text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted)] hover:text-[color:var(--ink)] cursor-pointer disabled:opacity-40"
                            >
                                {groupsLoading ? "Loading…" : "Load more"}
                            </button>
                        ) : null}
                    </div>
                </>
                )}
                </div>
            ) : null}

            {/* Not merely incomplete: an active integration syncing no scope puts the
                tenant in scoped posture with an empty allow-set, stopping capture. */}
            {!canSave ? (
                <p role="status" className={`${NOTE_CLASS} m-0 leading-[1.5]`}>
                    Until you pick the whole tenant or a group, this integration syncs no one. If no other integration is syncing, that also stops prompt capture for the tenant.
                </p>
            ) : null}

            <div className="flex items-center justify-between gap-[10px]">
                <span className={`${NOTE_CLASS} text-[11px]`}>
                    {syncFull
                        ? "Whole tenant"
                        : scopable && selected.size > 0
                          ? `${selected.size} ${selected.size === 1 ? "group" : "groups"} selected`
                          : "Nothing selected yet"}
                </span>
                <div className="flex items-center gap-[8px]">
                    {saveError ? <span className="text-red-400 text-[11px] truncate max-w-[280px]" title={saveError}>{saveError}</span> : null}
                    <button
                        type="button"
                        onClick={onSave}
                        disabled={saving || !canSave}
                        className={primaryBtnCls}
                    >
                        {saving ? "Saving…" : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
}

/**
 * What deleting this integration leaves behind in the customer's own consoles, or
 * `null` when it leaves nothing. A bring-your-own Okta token is revoked where they
 * made it and managed Entra is Origin's own app, so neither strands anything.
 */
export function deleteResidue(row) {
    if (row?.provider === "OKTA" && row?.managed) {
        return "The service app stays in your Okta. Deactivate it under Applications to cut off access.";
    }
    if (row?.provider === "GOOGLE") {
        return "Remove two things yourself: the grant under Domain-wide delegation in admin.google.com, and the service account key in your Google Cloud project. Until you do, anyone with the key can still read your directory.";
    }
    return null;
}

/** Derived from [`deleteResidue`] so the predicate and the copy cannot drift. */
export function deleteLeavesAnAppBehind(row) {
    return deleteResidue(row) !== null;
}

/**
 * Grey out an action and say why on hover, without the `disabled` attribute — which
 * in Chrome dispatches no mouse events, putting the explanation behind a tooltip that
 * never opens on the control being pointed at. The tooltip portals to <body>, so it
 * costs the row no width.
 */
function InertWhen({ frozen, reason, children }) {
    if (!frozen) return children;
    return (
        <HoverTooltip label={reason} placement="top" wide>
            <span tabIndex={0} className="inline-flex opacity-40 cursor-not-allowed">
                {cloneElement(children, {
                    onClick: undefined,
                    tabIndex: -1,
                    "aria-disabled": true,
                    className: `${children.props.className} pointer-events-none`,
                })}
            </span>
        </HoverTooltip>
    );
}

const WAIT_FOR_SYNC = "Wait for the running sync to finish.";

const SYNC_NOW_BLOCKED = {
    running: "A sync is already running. History shows when it started.",
    disabled: "This integration is disabled. Enable it under Edit to resume syncs.",
    pending: "This integration isn't connected yet. Finish setup under Edit first.",
    noScope: "No sync scope is configured. Choose Scope to sync the whole tenant or select groups.",
};

export const SCOPE_RESYNC_NOT_QUEUED =
    "Scope saved, but the re-sync could not be queued. The new scope applies at the next scheduled sync.";

export function syncRunning(row, syncState) {
    return syncState === "pending" || row?.status === "syncing";
}

function syncNowBlocked(row, running) {
    if (running) return SYNC_NOW_BLOCKED.running;
    if (row.status === "disabled") return SYNC_NOW_BLOCKED.disabled;
    if (row.status === "pending") return SYNC_NOW_BLOCKED.pending;
    if (row.syncScope === "not_configured") return SYNC_NOW_BLOCKED.noScope;
    return null;
}

export function solidAction(row, { running = false, canWrite = true, canSync = true } = {}) {
    if (running || row?.status === "deleting") return null;
    if (canWrite && ["pending", "at_risk", "error"].includes(row?.status)) return "edit";
    if (canSync && row?.status !== "disabled" && row?.syncScope === "not_configured") return "scope";
    return null;
}

function DeleteConfirm({ row, onConfirm, onCancel }) {
    const residue = deleteResidue(row);
    return (
        <div
            role="alert"
            className="grid gap-[8px] border border-[color:var(--severity-high)] bg-[color:color-mix(in_srgb,var(--severity-high)_7%,transparent)] px-[12px] py-[10px]"
        >
            <p className="m-0 text-[12px] leading-[1.45] text-[color:var(--ink)]">
                Delete {row.displayName || "this integration"}? Its synced users, groups and devices are removed from Origin.
            </p>
            {residue ? <p className="m-0 text-[12px] leading-[1.45] text-[color:var(--muted)]">{residue}</p> : null}
            <div className="flex flex-wrap justify-end gap-[8px]">
                <button type="button" onClick={onCancel} className={secondaryBtnCls}>
                    Cancel
                </button>
                <button type="button" onClick={() => onConfirm(row.id)} className={dangerBtnCls}>
                    Delete
                </button>
            </div>
        </div>
    );
}

export function CardActions({ row, syncState, inDeleteConfirm, onSync, onEdit, onShowScope, onShowHistory, onDeleteRequest, onDeleteConfirm, onCancelConfirm, canWrite = true, canManage = true, canSync = true }) {
    if (row.status === "deleting") return null;
    if (inDeleteConfirm) return <DeleteConfirm row={row} onConfirm={onDeleteConfirm} onCancel={onCancelConfirm} />;
    const running = syncRunning(row, syncState);
    const disabled = row.status === "disabled";
    const solid = solidAction(row, { running, canWrite, canSync });
    const syncBlocked = syncNowBlocked(row, running);
    const scopeBlocked = disabled
        ? "This integration is disabled. Enable it under Edit to change its sync scope."
        : running
          ? "Wait for the running sync to finish. A scope change can't apply until it does."
          : null;
    return (
        <div className="flex flex-wrap items-center gap-[8px] border-t border-[color:var(--line)] pt-[12px]">
            <button type="button" onClick={() => onShowHistory(row.id)} className={ghostBtnCls}>
                History
            </button>
            {canManage ? (
                <InertWhen frozen={running} reason={WAIT_FOR_SYNC}>
                    <button
                        type="button"
                        onClick={() => onDeleteRequest(row.id)}
                        className={`${ghostBtnCls} hover:text-[color:var(--severity-high)]`}
                    >
                        Delete
                    </button>
                </InertWhen>
            ) : null}
            <div className="ml-auto flex flex-wrap justify-end gap-[8px]">
                {canSync ? (
                    <InertWhen frozen={!!scopeBlocked} reason={scopeBlocked}>
                        <button
                            type="button"
                            onClick={() => onShowScope(row)}
                            className={solid === "scope" ? primaryBtnCls : secondaryBtnCls}
                        >
                            Scope
                        </button>
                    </InertWhen>
                ) : null}
                {canWrite ? (
                    <InertWhen frozen={running} reason={WAIT_FOR_SYNC}>
                        <button
                            type="button"
                            onClick={() => onEdit(row.id)}
                            className={solid === "edit" ? primaryBtnCls : secondaryBtnCls}
                        >
                            Edit
                        </button>
                    </InertWhen>
                ) : null}
                {canSync ? (
                    <InertWhen frozen={!!syncBlocked} reason={syncBlocked}>
                        <button type="button" onClick={() => onSync(row.id)} className={secondaryBtnCls}>
                            {running ? "Syncing…" : "Sync now"}
                        </button>
                    </InertWhen>
                ) : null}
            </div>
        </div>
    );
}

export function cardMessages(row, syncState) {
    const running = syncRunning(row, syncState);
    if (row.status === "deleting") {
        return [{ tone: "muted", text: "Removing synced directory data. This card disappears when it finishes." }];
    }
    if (running) {
        return row.lastSyncDate
            ? []
            : [{ tone: "muted", text: "A first sync of a large directory can take several minutes. History shows when this run started." }];
    }
    const messages = [];
    const startError = syncState && syncState !== "pending" ? syncState : null;
    const error = startError || row.lastSyncError;
    if (error) messages.push({ tone: error === SCOPE_RESYNC_NOT_QUEUED ? "warning" : "error", text: error });
    else if (row.status === "error") messages.push({ tone: "error", text: "The last sync failed. History has the details." });
    if (row.status === "at_risk") messages.push({ tone: "warning", text: statusHelp(row.status, row.provider) });
    else if (row.status === "pending") {
        messages.push({ tone: "muted", text: "Setup isn't finished, so nothing syncs yet. Open Edit to complete the connection." });
    } else if (row.status === "disabled") {
        messages.push({ tone: "muted", text: "Syncing is stopped. Enable it under Edit to resume." });
    } else if (row.syncScope === "not_configured" && !startError) {
        messages.push({ tone: "warning", text: "Connected but not syncing anything. Choose Scope to sync the whole tenant or pick groups." });
    }
    return messages;
}

const MESSAGE_TONE = {
    error: "text-[color:var(--severity-high)]",
    warning: "text-[color:var(--severity-medium)]",
    muted: "text-[color:var(--muted)]",
};

function CardMessage({ tone, text }) {
    const ref = useRef(null);
    const [overflows, setOverflows] = useState(false);
    const [expanded, setExpanded] = useState(false);
    useLayoutEffect(() => {
        const el = ref.current;
        if (el && !expanded) setOverflows(el.scrollHeight > el.clientHeight + 1);
    }, [text, expanded]);
    return (
        <div className="grid justify-items-start gap-[4px]">
            <p
                ref={ref}
                role={tone === "error" ? "alert" : undefined}
                className={`m-0 text-[12px] leading-[1.45] [overflow-wrap:anywhere] ${MESSAGE_TONE[tone]} ${expanded ? "" : "line-clamp-2"}`}
            >
                {text}
            </p>
            {overflows || expanded ? (
                <button type="button" onClick={() => setExpanded((v) => !v)} className={`${linkCls} text-[11px]`}>
                    {expanded ? "Show less" : "Show full error"}
                </button>
            ) : null}
        </div>
    );
}

export function IntegrationCard({ row, syncState, ...actions }) {
    const pill = statusPill(row.status);
    const setup = row.status === "pending";
    const credential = credentialFact(row.status);
    const scopeLabel = SYNC_SCOPE_LABEL[row.syncScope];
    const titleId = `integration-${row.id}-title`;
    return (
        <article
            aria-labelledby={titleId}
            className={[
                "flex min-w-0 flex-col gap-[14px] border p-[16px]",
                setup ? "border-dashed border-[color:var(--line-strong)] bg-transparent" : "bg-[color:var(--panel)]",
                row.status === "error" ? "border-[color:var(--severity-high)]" : setup ? "" : "border-[color:var(--line)]",
                row.status === "deleting" ? "opacity-60" : "",
            ].join(" ")}
        >
            <div className="flex items-start gap-[12px] max-[560px]:flex-wrap">
                <SourceLogo logo={PROVIDER_LOGO[row.provider]} muted={setup} />
                <div className="grid min-w-0 flex-1 gap-[2px]">
                    <h3
                        id={titleId}
                        className="m-0 flex flex-wrap items-center gap-[6px] text-[14px] font-medium leading-[1.2] text-[color:var(--ink)] [overflow-wrap:anywhere]"
                    >
                        {row.displayName || PROVIDER_LABEL[row.provider] || "Directory"}
                        {row.isPrimary ? <PrimaryBadge /> : null}
                    </h3>
                    <span className={NOTE_CLASS}>{integrationDescription(row)}</span>
                </div>
                <span className="max-[560px]:order-last max-[560px]:basis-full max-[560px]:pl-[40px]">
                    <StatusPill tone={pill.tone} label={pill.label} pulse={!!pill.pulse} />
                </span>
            </div>
            {credential ? (
                <dl className="m-0 grid grid-cols-3 gap-[12px] max-[560px]:grid-cols-2">
                    <Fact
                        label="Scope"
                        tone={row.syncScope === "not_configured" ? "warning" : scopeLabel ? null : "muted"}
                    >
                        {scopeLabel ?? "—"}
                    </Fact>
                    <Fact label="Last sync" tone={row.lastSyncDate ? null : "muted"}>
                        {lastSyncLabel(row.lastSyncDate)}
                    </Fact>
                    <Fact label="Credential" tone={credential.tone}>
                        {credential.text}
                    </Fact>
                </dl>
            ) : null}
            {cardMessages(row, syncState).map((message) => (
                <CardMessage key={message.text} tone={message.tone} text={message.text} />
            ))}
            <CardActions row={row} syncState={syncState} {...actions} />
        </article>
    );
}

const textareaCls =
    "w-full rounded-[2px] border border-[color:var(--line-strong)] bg-transparent px-[10px] py-[8px] font-mono text-[11px] leading-[1.4] text-[color:var(--ink)] outline-none placeholder:text-[color:var(--muted-soft)] focus:border-[color:var(--ink)] resize-y";

/**
 * What a managed Okta row is still missing before it can connect. Created from the
 * org domain alone, it sits inert — the scheduler only picks up connected rows —
 * and nothing else on the page says so.
 */
export function oktaSetupPending({ provider, managed, status, clientId }) {
    return provider === "OKTA" && !!managed && status === "pending" && !clientId?.trim();
}

const OKTA_APP_SCOPES = [
    { scope: "okta.users.read", note: "people and their status" },
    { scope: "okta.groups.read", note: "groups and membership" },
    { scope: "okta.devices.read", note: "registered devices" },
    { scope: "okta.apps.read", note: "reads this app's own name, to prove you own the org" },
];

/**
 * Mirrors `GOOGLE_DWD_SCOPES` in
 * `backend/crates/directory-sync/src/providers/google/mod.rs`. This is the list an
 * admin pastes into the Admin console, so drift grants the wrong set — which
 * surfaces as failed or incomplete syncing, with no error naming the missing scope.
 */
export const GOOGLE_DWD_SCOPES = [
    { scope: "admin.directory.user.readonly", note: "people and their status" },
    { scope: "admin.directory.group.readonly", note: "groups" },
    { scope: "admin.directory.group.member.readonly", note: "group membership" },
    { scope: "admin.directory.orgunit.readonly", note: "organizational units" },
    { scope: "admin.directory.rolemanagement.readonly", note: "which accounts are super-admins or delegated admins" },
    { scope: "admin.directory.domain.readonly", note: "secondary domains, so people on them are not counted as separate identities" },
    { scope: "admin.directory.userschema.readonly", note: "custom user attributes such as employee ID or cost centre" },
    { scope: "cloud-identity.devices.readonly", note: "registered devices, so a person can be attached to a machine" },
    { scope: "admin.reports.audit.readonly", note: "the audit log, which is how Origin catches up without re-reading the whole directory" },
];

const GOOGLE_SCOPE_PREFIX = "https://www.googleapis.com/auth/";

/** The exact string the Admin console's delegation dialog expects. */
export const GOOGLE_SCOPE_CSV = GOOGLE_DWD_SCOPES.map(({ scope }) => `${GOOGLE_SCOPE_PREFIX}${scope}`).join(",");

function ManagedMicrosoftPermissionsPanel() {
    return (
        <PermissionList
            title={`View the ${MANAGED_MICROSOFT_PERMISSION_COUNT} permissions Origin's app requests (all read-only)`}
            groups={MANAGED_MICROSOFT_PERMISSION_GROUPS}
            note={MANAGED_MICROSOFT_PERMISSIONS_NOTE}
        />
    );
}

function EntraPermissionsPanel() {
    return (
        <PermissionList
            title={`View the ${ENTRA_DIRECTORY_PERMISSIONS.length} permissions Origin requests (all read-only)`}
            groups={[{ permissions: ENTRA_DIRECTORY_PERMISSIONS }]}
            note="Add these as Application permissions, not Delegated. If the app already has Directory.Read.All, you can skip Group.Read.All and Organization.Read.All."
        />
    );
}

function GooglePermissionsPanel() {
    return (
        <PermissionList
            title={`View the ${GOOGLE_DWD_SCOPES.length} scopes Origin requests (all read-only)`}
            groups={[{ permissions: GOOGLE_DWD_SCOPES }]}
            note="If this list grows, syncing stops until an admin grants the new list to the same client ID."
        />
    );
}

function OktaPermissionsPanel() {
    return (
        <PermissionList
            title={`View the ${OKTA_APP_SCOPES.length} scopes Origin requests (all read-only)`}
            groups={[{ permissions: OKTA_APP_SCOPES }]}
            note="The app also needs the Read-only Administrator role. Without it, these scopes read nothing."
        />
    );
}

function oktaHost(orgDomain) {
    return orgDomain?.replace(/^https?:\/\//, "").replace(/\/+$/, "") || "your Okta org";
}

function CopyRow({ label, value }) {
    return (
        <div className="mt-[6px]">
            <CopyValue label={label} value={value} />
        </div>
    );
}

function EntraAppGuide({ redirectUri }) {
    return (
        <div className="grid gap-[12px]">
            <Instructions>
                <li>In the Azure portal, open <b>Microsoft Entra ID</b> → <b>App registrations</b> → <b>New registration</b>. Any name works.</li>
                <li>Under <b>Certificates &amp; secrets</b>, create a client secret. Copy its <b>Value</b>, not its ID. It is shown only once.</li>
                <li>Under <b>API permissions</b>, add the Microsoft Graph <b>Application permissions</b> listed below.</li>
                <li>Click <b>Grant admin consent</b>. Without it, every call returns 403.</li>
                <li>
                    Under <b>Authentication</b>, add a <b>Web</b> platform with this redirect URI.
                    <CopyRow label="Redirect URI" value={redirectUri} />
                </li>
            </Instructions>
            <EntraPermissionsPanel />
        </div>
    );
}

function OktaTokenGuide({ orgDomain }) {
    return (
        <div className="grid gap-[10px]">
            <Instructions>
                <li>Sign in to <b>{oktaHost(orgDomain)}</b> as a read-only administrator. A service account works best.</li>
                <li>Go to <b>Security</b> → <b>API</b> → <b>Tokens</b> → <b>Create token</b>. Copy it now. Okta shows it only once.</li>
            </Instructions>
            <p className={`${NOTE_CLASS} m-0 leading-[1.5]`}>
                A token cannot be scope-limited, so it has the same access as the account that made it. It stops working after 30 days of no use, or when that account is deactivated.
            </p>
        </div>
    );
}

function GoogleCloudGuide() {
    return (
        <Instructions>
            <li>In Google Cloud, choose a project and enable the <b>Admin SDK API</b> and the <b>Cloud Identity API</b>. Without Cloud Identity, devices never sync.</li>
            <li>Under <b>IAM &amp; Admin</b> → <b>Service Accounts</b>, create a service account.</li>
            <li>Open it and go to <b>Keys</b> → <b>Add key</b> → <b>Create new key</b> → <b>JSON</b>. An OAuth client JSON looks similar but will not work.</li>
            <li>On its <b>Details</b> tab, copy the <b>Unique ID</b>, the long number.</li>
        </Instructions>
    );
}

function GoogleDelegationGuide() {
    return (
        <div className="grid gap-[12px]">
            <Instructions>
                <li>In admin.google.com, go to <b>Security</b> → <b>Access and data control</b> → <b>API controls</b> → <b>Domain-wide delegation</b> → <b>Add new</b>.</li>
                <li>Paste the service account&apos;s Unique ID as the client ID.</li>
                <li>
                    Paste all of these scopes at once. If one is missing, Google&apos;s error does not say which.
                    <CopyRow label="OAuth scopes" value={GOOGLE_SCOPE_CSV} />
                </li>
            </Instructions>
            <GooglePermissionsPanel />
        </div>
    );
}

function OktaAppGuide({ orgDomain, bindingNonce, jwksUrl }) {
    return (
        <div className="grid gap-[12px]">
            <Instructions>
                <li>In <b>{oktaHost(orgDomain)}</b>, go to <b>Applications</b> → <b>Create App Integration</b> → <b>API Services</b>.</li>
                <li>
                    Name the app exactly this. Origin reads the name to confirm you own the org.
                    {bindingNonce ? (
                        <CopyRow label="App name" value={bindingNonce} />
                    ) : (
                        <span className="block text-[color:var(--severity-high)]">The name isn&apos;t available. Go back and reopen this integration.</span>
                    )}
                </li>
                <li>
                    Under <b>Client Credentials</b>, choose <b>Public key / Private key</b>, then <b>Use a URL to fetch keys dynamically</b>, and paste this URL.
                    {jwksUrl ? (
                        <CopyRow label="Public key URL" value={jwksUrl} />
                    ) : (
                        <span className="block text-[color:var(--severity-high)]">This server has no key URL. Ask your Origin administrator for it.</span>
                    )}
                </li>
                <li>Leave <b>Require DPoP</b> off. Okta rejects Origin&apos;s sign-in with it on.</li>
                <li>Grant the {OKTA_APP_SCOPES.length} scopes below, then assign the app the <b>Read-only Administrator</b> role.</li>
                <li>Optional: under <b>Application notes for admins</b>, note that this app runs Origin&apos;s directory sync, so no one deletes it by mistake.</li>
            </Instructions>
            <OktaPermissionsPanel />
        </div>
    );
}

export function OktaClientIdForm({ integrationId, displayName, orgDomain, clientId, onClientIdChange, onConnected }) {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const onConnect = useCallback(async () => {
        setBusy(true);
        setError(null);
        try {
            // Scope is reachable on a pending row, so this row may already hold a
            // selection to preserve through the wholesale config replace. Re-read
            // rather than trust the parent's copy, which can be stale.
            const stored = await getDirectoryIntegration(integrationId);
            const storedOkta = stored?.config?.okta ?? {};
            await updateDirectoryIntegration(integrationId, {
                displayName,
                provider: "OKTA",
                config: {
                    ...camelKeysToSnake(storedOkta),
                    org_domain: orgDomain,
                    client_id: clientId.trim(),
                },
                syncFullTenant: !!stored?.config?.syncFullTenant,
            });
            const r = await testDirectoryIntegration(integrationId);
            if (!r?.success) throw new Error(r?.message || "Okta rejected the connection.");
            onConnected?.();
        } catch (err) {
            setError(humanError(err, "Could not connect. Check the app in Okta and try again."));
        } finally {
            setBusy(false);
        }
    }, [integrationId, displayName, orgDomain, clientId, onConnected]);

    return (
        <Panel className="grid gap-[12px] p-[14px]">
            <Field label="Client ID" hint="On the app's General tab in Okta.">
                <input
                    type="text"
                    value={clientId}
                    spellCheck={false}
                    onChange={(e) => onClientIdChange(e.target.value)}
                    placeholder="0oa1b2c3d4EXAMPLE5"
                    className={`${inputCls} font-mono`}
                />
            </Field>
            <div>
                <button type="button" onClick={onConnect} disabled={busy || !clientId.trim()} className={primaryBtnCls}>
                    {busy ? "Connecting…" : "Connect"}
                </button>
            </div>
            <ErrorText>{error}</ErrorText>
        </Panel>
    );
}

/**
 * Whether an integration authorizes AI-prompt capture for its tenant. Mirrors
 * `reduce_posture` in shared-prompt-sync: an unconnected, disabled, or torn-down row
 * authorizes nothing, and neither does one syncing no scope.
 */
export function authorizesCapture(row) {
    if (["pending", "disabled", "deleting"].includes(row?.status)) return false;
    return row?.syncScope !== "not_configured";
}

/** Must match `resolve_status_transition`: only a row that connected once can toggle. */
export function canToggleSync(status) {
    return ["connected", "syncing", "at_risk", "error", "disabled"].includes(status);
}

/**
 * Stop or resume syncing, from the one place an admin already goes to change an
 * integration. Deliberate by construction: disabling asks for confirmation and
 * states what it costs, because it stops directory syncing AND — when it's the
 * last integration authorizing capture — AI-prompt capture with it.
 */
function SyncSwitch({ integrationId, status, lastAuthorizingCapture, onChanged }) {
    const [confirming, setConfirming] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const disabled = status === "disabled";

    const apply = useCallback(async (enabled) => {
        setBusy(true);
        setError(null);
        try {
            await setDirectoryIntegrationEnabled(integrationId, enabled);
            onChanged?.();
        } catch (err) {
            setError(humanError(err, enabled ? "Failed to enable the integration" : "Failed to disable the integration"));
            setBusy(false);
        }
    }, [integrationId, onChanged]);

    return (
        <Panel className="grid gap-[10px] p-[14px]">
            <div className="flex flex-wrap items-center justify-between gap-[10px]">
                <span className="grid min-w-0 gap-[2px]">
                    <span className="text-[12px] font-medium text-[color:var(--ink)]">
                        {disabled ? "Syncing is off" : "Syncing is on"}
                    </span>
                    <span className={`${NOTE_CLASS} leading-[1.45]`}>
                        {disabled
                            ? "Every sync skips this integration. Enabling it starts a sync right away."
                            : "Origin syncs this directory on a schedule and when you ask."}
                    </span>
                </span>
                {disabled ? (
                    <button type="button" onClick={() => apply(true)} disabled={busy} className={primaryBtnCls}>
                        {busy ? "Enabling…" : "Enable"}
                    </button>
                ) : confirming ? null : (
                    <button type="button" onClick={() => setConfirming(true)} className={secondaryBtnCls}>
                        Disable
                    </button>
                )}
            </div>

            {confirming && !disabled ? (
                <div className="grid gap-[8px] border-t border-[color:var(--line)] pt-[10px]">
                    <p className="m-0 text-[12px] leading-[1.45] text-[color:var(--ink)]">
                        Scheduled and manual syncs stop. Synced data stays, but people who leave the directory stay listed until you enable it again.
                        {lastAuthorizingCapture
                            ? " This is the only integration syncing a directory, so disabling it also stops prompt capture for this tenant."
                            : null}
                    </p>
                    <div className="flex flex-wrap justify-end gap-[8px]">
                        <button type="button" onClick={() => setConfirming(false)} className={secondaryBtnCls}>
                            Cancel
                        </button>
                        <button type="button" onClick={() => apply(false)} disabled={busy} className={dangerBtnCls}>
                            {busy ? "Disabling…" : "Disable syncing"}
                        </button>
                    </div>
                </div>
            ) : null}

            <ErrorText>{error}</ErrorText>
        </Panel>
    );
}

export function IntegrationForm({ integrationId, existing, directories = [], lastAuthorizingCapture = false, onSaved, onScopeSaved, onBack }) {
    const isEdit = !!integrationId;
    // Test triggers a live connection probe — an execute.sync action.
    const formCaps = useMyPermissions();
    const canTest = hasPermission(formCaps, PERMISSIONS.INTEGRATIONS_EXECUTE_SYNC);
    // Re-authorize re-runs the OAuth/admin-consent handshake on an existing row.
    const canWriteForm = hasPermission(formCaps, PERMISSIONS.INTEGRATIONS_WRITE);
    const [provider, setProvider] = useState(existing?.provider ?? null);
    const [displayName, setDisplayName] = useState(existing?.displayName ?? "");
    const [config, setConfig] = useState({});
    const [credentials, setCredentials] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [testStatus, setTestStatus] = useState(null);
    const [managed, setManaged] = useState(!!integrationId && !!existing?.managed);
    const [showManual, setShowManual] = useState(false);
    const [connectedId, setConnectedId] = useState(null);
    const [oktaClientId, setOktaClientId] = useState("");
    // A managed Okta row with no app behind it yet, waiting on a Client ID. Set
    // after a service-app create, and on opening a row left half-configured.
    const [oktaSetup, setOktaSetup] = useState(null);
    // In edit mode the managed/BYO distinction isn't known until the detail
    // fetch resolves. Gate the provider-field section on it so we never render
    // the BYO fields and then yank them when the row turns out to be managed.
    const [detailLoaded, setDetailLoaded] = useState(!integrationId);
    const [detailError, setDetailError] = useState(null);
    // Sent only once the config read has landed, so a save cannot unmark a
    // primary the form never saw.
    const [isPrimary, setIsPrimary] = useState(false);
    // Has no wire presence, so omitting it clears it — and a cleared flag with a
    // stored group selection reads as scoped, which tombstones on the next sync.
    const [syncFullTenant, setSyncFullTenant] = useState(false);
    const [copilotSources, setCopilotSources] = useState([]);
    const [copyFromCopilot, setCopyFromCopilot] = useState(null);
    const onSavedRef = useRef(onSaved);
    useEffect(() => { onSavedRef.current = onSaved; }, [onSaved]);
    const finishConnect = useCallback((id, connectedProvider) => {
        if (id) setConnectedId(id);
        onSavedRef.current?.({ id, provider: connectedProvider });
    }, []);

    // Tracks a speculative row created for an in-flight OAuth connect so it can be
    // cleaned up on abandonment. `{ id, managed }`; null once consent resolves
    // (success/failure) or is cancelled. Only *managed* rows are auto-deleted on
    // abandonment — a BYO row holds the admin's entered app config and may be left
    // mid-setup deliberately, so it persists until they finish or delete it.
    const pendingCleanupRef = useRef(null);
    const providerRef = useRef(provider);
    providerRef.current = provider;
    const { pendingId: pendingOauthId, start: startConsent, cancel: cancelConsent } = useDirectoryOauthPopup({
        onResult: ({ integrationId: id, status, message }) => {
            const cleanup = pendingCleanupRef.current;
            pendingCleanupRef.current = null;
            if (status === "success") {
                if (isEdit) onSavedRef.current?.();
                else finishConnect(id, providerRef.current);
                return;
            }
            if (shouldCleanupAbandoned(cleanup)) deleteDirectoryIntegration(id).catch(() => {});
            setError(message ?? "Provider did not complete admin consent.");
        },
        onClosedWithoutResult: () => {
            pendingCleanupRef.current = null;
            setError(
                "The consent tab closed before consent was confirmed. Go back and check this integration. If it still says Not connected, try again.",
            );
        },
    });

    useEffect(() => {
        if (isEdit) return undefined;
        let cancelled = false;
        listCopilotIntegrations()
            .then((response) => {
                if (cancelled) return;
                setCopilotSources((response?.integrations ?? []).filter((integration) => integration.status === "connected"));
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [isEdit]);
    // Managed connect abandoned by closing the modal / navigating away (no consent
    // callback fires): delete the speculative row on unmount so it doesn't linger
    // as `pending` and block a same-name retry. Success/failure/cancel null the ref
    // first, so this only fires on true abandonment.
    useEffect(() => () => {
        const p = pendingCleanupRef.current;
        if (shouldCleanupAbandoned(p)) deleteDirectoryIntegration(p.id).catch(() => {});
    }, []);

    // In edit mode, load the stored provider config so a save sends the
    // COMPLETE config. The backend replaces config in full, so a partial would
    // wipe stored fields (tenant_id, synced_group_ids, …). Without this the
    // edit form's config starts empty and either silently discards edits or
    // wipes — both wrong.
    useEffect(() => {
        if (!isEdit || !provider) return;
        let cancelled = false;
        getDirectoryIntegration(integrationId)
            .then((detail) => {
                if (cancelled) return;
                const key = PROVIDER_CONFIG_KEY[provider];
                const cfg = key ? detail?.config?.[key] : null;
                if (cfg && typeof cfg === "object") setConfig(camelKeysToSnake(cfg));
                setManaged(!!detail?.integration?.managed);
                // Wrapper-level, not part of the provider variant.
                setIsPrimary(!!detail?.config?.isPrimary);
                setSyncFullTenant(!!detail?.config?.syncFullTenant);
                if (oktaSetupPending({
                    provider,
                    managed: detail?.integration?.managed,
                    status: detail?.integration?.status,
                    clientId: cfg?.clientId,
                })) {
                    setOktaSetup({
                        id: integrationId,
                        orgDomain: cfg?.orgDomain ?? "",
                        bindingNonce: cfg?.bindingNonce ?? "",
                        jwksUrl: detail?.oktaJwksUrl ?? "",
                    });
                }
                setDetailLoaded(true);
            })
            .catch((err) => {
                if (!cancelled) setDetailError(humanError(err, "Couldn't load this integration's settings."));
            });
        return () => { cancelled = true; };
    }, [isEdit, provider, integrationId]);

    // Managed Entra hides both field groups — Origin owns that app registration and
    // its cert. Managed Okta's app lives in the customer's org, so the org domain is
    // still theirs to enter and only the credential group goes.
    const copying = !!copyFromCopilot && provider === "ENTRA_ID" && !isEdit;
    const managedEntra = managed && provider === "ENTRA_ID" && !copying;
    const oktaServiceApp = managed && provider === "OKTA";
    const fields = provider && !managedEntra && !copying && detailLoaded ? PROVIDER_FIELDS[provider] ?? [] : [];
    // Credentials are entered on create and re-entered on edit to rotate an
    // expired/rotated secret. On edit they're OPTIONAL: the stored secret is never
    // read back (Get omits it), so a blank field means "keep the current secret"
    // and only a filled field rotates it. Gate on detailLoaded like `fields` so a
    // managed row (known only after the detail fetch) never flashes the BYO inputs.
    const credentialFields = useMemo(() => {
        if (!provider || managedEntra || oktaServiceApp || copying || !detailLoaded) return [];
        const base = PROVIDER_CREDENTIALS[provider] ?? [];
        return isEdit ? base.map((f) => ({ ...f, required: false })) : base;
    }, [provider, managedEntra, oktaServiceApp, copying, detailLoaded, isEdit]);
    const isOauth = !!provider && OAUTH_PROVIDERS.has(provider);

    const fieldErrors = useMemo(() => {
        const errs = {};
        if (!displayName.trim()) errs["Display Name"] = "required";
        for (const f of fields) {
            const e = validateField(provider, f, (config[f.key] ?? "").trim());
            if (e) errs[f.label] = e;
        }
        for (const f of credentialFields) {
            const e = validateField(provider, f, (credentials[f.key] ?? "").trim());
            if (e) errs[f.label] = e;
        }
        return errs;
    }, [provider, displayName, config, credentials, fields, credentialFields]);

    const errorList = Object.entries(fieldErrors).map(([k, v]) => `${k}: ${v}`);
    const canSubmit = !isSaving && !pendingOauthId && errorList.length === 0 && !!provider;

    const onConnectOauth = useCallback(async () => {
        setIsSaving(true);
        setError(null);
        // The row is created before the consent handshake (the OAuth state binds
        // to its id), so any failure after the create must roll it back — else an
        // aborted/failed handshake (e.g. the auth-url step erroring) leaves an
        // orphaned `pending` integration. Hoisted so the catch can clean up.
        let newId = null;
        try {
            // Managed Entra is signalled by a config carrying no `tenant_id`. Derive
            // the payload from the shape rather than trusting the form to have been
            // cleared: a `tenant_id` left over from a bring-your-own attempt makes the
            // backend infer BYO and build a row whose consent has no secret behind it.
            const created = await createDirectoryIntegration({
                displayName,
                provider,
                config: managedEntra ? {} : config,
                credentials: managedEntra ? {} : credentials,
            });
            newId = created?.integration?.id;
            if (!newId) throw new Error("integration created but no id returned");
            const redirectUri = directoryOauthRedirectUri(window.location.origin);
            const { authUrl } = await getDirectoryAuthUrl({ integrationId: newId, redirectUri });
            if (!startConsent({ integrationId: newId, provider, redirectUri, authUrl })) {
                try { await deleteDirectoryIntegration(newId); } catch {}
                setError("Your browser blocked the consent tab. Allow popups for this site, then try again.");
                setIsSaving(false);
                return;
            }
            pendingCleanupRef.current = { id: newId, managed };
            setIsSaving(false);
        } catch (err) {
            // Roll back the speculative row if the create succeeded before the failure.
            if (newId) { try { await deleteDirectoryIntegration(newId); } catch {} }
            pendingCleanupRef.current = null;
            cancelConsent();
            setError(humanError(err, "Failed to start OAuth flow"));
            setIsSaving(false);
        }
    }, [displayName, provider, config, credentials, managed, managedEntra, startConsent, cancelConsent]);

    const onDirectCreate = useCallback(async () => {
        setIsSaving(true);
        setError(null);
        try {
            const created = await createDirectoryIntegration({ displayName, provider, config, credentials });
            finishConnect(created?.integration?.id, provider);
        } catch (err) {
            setError(humanError(err, "Failed to create integration"));
        } finally {
            setIsSaving(false);
        }
    }, [displayName, provider, config, credentials, finishConnect]);

    // Hold the form open on the returned code instead of closing: it is the only
    // place that code is shown, and the row cannot connect without a Client ID.
    const onCreateOktaServiceApp = useCallback(async () => {
        setIsSaving(true);
        setError(null);
        try {
            const created = await createDirectoryIntegration({
                displayName,
                provider,
                config,
                credentials: {},
            });
            const newId = created?.integration?.id;
            if (!newId) throw new Error("integration created but no id returned");
            const detail = await getDirectoryIntegration(newId);
            const cfg = detail?.config?.okta ?? {};
            setOktaSetup({
                id: newId,
                orgDomain: cfg.orgDomain ?? config.org_domain ?? "",
                bindingNonce: cfg.bindingNonce ?? "",
                jwksUrl: detail?.oktaJwksUrl ?? "",
            });
        } catch (err) {
            setError(humanError(err, "Failed to create integration"));
        } finally {
            setIsSaving(false);
        }
    }, [displayName, provider, config]);

    const onUpdate = useCallback(async () => {
        setIsSaving(true);
        setError(null);
        try {
            // Send only the credential fields the admin actually re-entered
            // (see buildCredentialPatch). A blank field is omitted so the backend
            // leaves that secret unchanged — it merges the submitted keys onto the
            // stored blob, never replacing the keys we don't send.
            await updateDirectoryIntegration(integrationId, {
                displayName,
                provider,
                config,
                credentials: buildCredentialPatch(credentialFields, credentials),
                isPrimary: detailLoaded ? isPrimary : undefined,
                syncFullTenant: detailLoaded ? syncFullTenant : undefined,
            });
            onSavedRef.current();
        } catch (err) {
            setError(humanError(err, "Failed to save integration"));
        } finally {
            setIsSaving(false);
        }
    }, [integrationId, displayName, provider, config, credentials, credentialFields, detailLoaded, isPrimary, syncFullTenant]);

    const onCopyFromCopilot = useCallback(async () => {
        setIsSaving(true);
        setError(null);
        try {
            const created = await createDirectoryIntegration({
                displayName,
                provider,
                config: {},
                credentials: {},
                copyFromCopilotIntegrationId: copyFromCopilot.id,
            });
            finishConnect(created?.integration?.id, provider);
        } catch (err) {
            setError(humanError(err, "Failed to create integration"));
        } finally {
            setIsSaving(false);
        }
    }, [displayName, provider, copyFromCopilot, finishConnect]);

    const onSave = () => {
        if (!provider) return;
        if (isEdit) return onUpdate();
        if (copying) return onCopyFromCopilot();
        if (isOauth && managed) return onConnectOauth();
        if (oktaServiceApp) return onCreateOktaServiceApp();
        return onDirectCreate();
    };

    const onTest = useCallback(async () => {
        if (!integrationId) return;
        setTestStatus("testing");
        try {
            const r = await testDirectoryIntegration(integrationId);
            setTestStatus(r?.success ? "ok" : "fail");
        } catch {
            setTestStatus("fail");
        }
    }, [integrationId]);

    const onReauthorize = useCallback(async () => {
        if (!integrationId) return;
        setIsSaving(true);
        setError(null);
        try {
            const redirectUri = directoryOauthRedirectUri(window.location.origin);
            const { authUrl } = await getDirectoryAuthUrl({ integrationId, redirectUri });
            if (!startConsent({ integrationId, provider, redirectUri, authUrl })) {
                setError("Your browser blocked the consent tab. Allow popups for this site, then try again.");
                setIsSaving(false);
                return;
            }
            setIsSaving(false);
        } catch (err) {
            cancelConsent();
            setError(humanError(err, "Failed to start re-authorization"));
            setIsSaving(false);
        }
    }, [integrationId, provider, startConsent, cancelConsent]);

    const cancelOauth = () => {
        const id = pendingOauthId;
        pendingCleanupRef.current = null;
        cancelConsent();
        // Only a fresh (create-mode) connect's speculative row is deleted on cancel.
        // Cancelling a re-authorize (edit mode) just aborts it — the existing
        // integration must never be deleted.
        if (id && cancelDeletesSpeculativeRow(isEdit)) deleteDirectoryIntegration(id).catch(() => {});
    };

    const choices = [
        ...copilotSources.map((source) => ({
            key: `copilot:${source.id}`,
            provider: "ENTRA_ID",
            managed: false,
            copilot: source,
            title: "Use your Microsoft 365 Copilot connection",
            description:
                copilotSources.length > 1 && source.entraTenantId
                    ? `Tenant ${source.entraTenantId}. Reuses its Microsoft app and admin consent.`
                    : "Reuses its Microsoft app and admin consent.",
            disabledReason: directoryHoldsTenant(directories, source.entraTenantId) ? ENTRA_ALREADY_CONNECTED : null,
        })),
        ...SETUP_CHOICES,
    ];
    const selectedKey = copyFromCopilot ? `copilot:${copyFromCopilot.id}` : integrationKindKey(provider, managed);
    const selected = [...choices, ...MANUAL_CHOICES].find((c) => c.key === selectedKey) ?? null;
    const manualSelected = MANUAL_CHOICES.some((c) => c.key === selectedKey);
    const manualOpen = showManual || manualSelected;
    const choose = (next) => {
        if (next.provider !== provider) {
            setConfig({});
            setCredentials({});
        }
        if (!displayName.trim() || displayName === defaultName(directories, provider)) {
            setDisplayName(defaultName(directories, next.provider));
        }
        if (manualSelected || MANUAL_CHOICES.includes(next)) setShowManual(true);
        setProvider(next.provider);
        setManaged(next.managed);
        setCopyFromCopilot(next.copilot ?? null);
        setError(null);
    };

    const connected = !!connectedId;
    const locked = connected || !!oktaSetup;
    const visibleError = (message) => (message === "required" ? null : message);
    const typed =
        fields.some((f) => config[f.key]?.trim()) || credentialFields.some((f) => credentials[f.key]?.trim());

    const nameField = (
        <Field label="Display name" error={visibleError(fieldErrors["Display Name"])}>
            <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Acme directory"
                className={inputCls}
            />
        </Field>
    );

    const detailInputs = (
        <div className="grid gap-[12px] sm:grid-cols-2">
            {fields.map((f) => (
                <Field key={f.key} label={f.label} hint={f.hint} error={visibleError(fieldErrors[f.label])}>
                    <input
                        type={f.type}
                        value={config[f.key] ?? ""}
                        spellCheck={false}
                        onChange={(e) => setConfig((cur) => ({ ...cur, [f.key]: e.target.value }))}
                        placeholder={f.placeholder}
                        className={inputCls}
                    />
                </Field>
            ))}
            {credentialFields.map((f) => (
                <div key={f.key} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
                    <Field
                        label={f.label}
                        hint={isEdit ? "Leave blank to keep the current one." : f.hint ?? "Stored encrypted."}
                        error={visibleError(fieldErrors[f.label])}
                    >
                        {f.type === "textarea" ? (
                            <textarea
                                rows={6}
                                autoComplete="new-password"
                                spellCheck={false}
                                value={credentials[f.key] ?? ""}
                                onChange={(e) => setCredentials((cur) => ({ ...cur, [f.key]: e.target.value }))}
                                placeholder={f.placeholder}
                                className={textareaCls}
                            />
                        ) : (
                            <input
                                type={f.type}
                                autoComplete="new-password"
                                spellCheck={false}
                                value={credentials[f.key] ?? ""}
                                onChange={(e) => setCredentials((cur) => ({ ...cur, [f.key]: e.target.value }))}
                                placeholder={f.placeholder}
                                className={`${inputCls} font-mono`}
                            />
                        )}
                    </Field>
                </div>
            ))}
        </div>
    );

    const submitLabel = isSaving
        ? isEdit ? "Saving…" : "Connecting…"
        : isEdit ? "Save changes"
        : managedEntra || copying ? "Connect Microsoft Entra"
        : oktaServiceApp ? "Continue"
        : "Connect";

    const consentFirst = reauthorizeVisible(isEdit, isOauth, canWriteForm) && existing?.status === "pending";

    const submitRow = (
        <div className="grid gap-[8px]">
            {pendingOauthId ? (
                <ConsentWait onCancel={cancelOauth} />
            ) : (
                <div className="flex flex-wrap items-center gap-[8px]">
                    {consentFirst ? (
                        <button type="button" onClick={onReauthorize} className={primaryBtnCls} disabled={isSaving}>
                            Grant admin consent
                        </button>
                    ) : null}
                    <button type="button" onClick={onSave} disabled={!canSubmit} className={consentFirst ? secondaryBtnCls : primaryBtnCls}>
                        {submitLabel}
                    </button>
                    {isEdit && canTest ? (
                        <button type="button" onClick={onTest} className={secondaryBtnCls} disabled={testStatus === "testing"}>
                            {testStatus === "testing" ? "Testing…" : "Test connection"}
                        </button>
                    ) : null}
                    {reauthorizeVisible(isEdit, isOauth, canWriteForm) && !consentFirst ? (
                        <button type="button" onClick={onReauthorize} className={secondaryBtnCls} disabled={isSaving}>
                            Re-authorize
                        </button>
                    ) : null}
                    {!canSubmit && !isSaving && errorList.length > 0 ? (
                        <span className={NOTE_CLASS}>Fill in the fields above to continue.</span>
                    ) : null}
                </div>
            )}
            {testStatus === "ok" ? (
                <span className="text-[12px] text-[color:var(--green)]">Connection works.</span>
            ) : testStatus === "fail" ? (
                <span className="text-[12px] text-[color:var(--severity-high)]">Connection failed. Check the details above.</span>
            ) : null}
            <ErrorText>{error}</ErrorText>
        </div>
    );

    const scopeStep = {
        key: "scope",
        title: "Choose what to sync",
        summary: connected ? null : "Available once the connection is made.",
        complete: false,
        body: connected ? (
            <Panel className="p-[14px]">
                <GroupScopeView
                    integrationId={connectedId}
                    provider={provider}
                    onSaved={(res) => onScopeSaved?.({ id: connectedId, ...res })}
                />
            </Panel>
        ) : null,
    };

    const oktaAppSteps = [
        {
            key: "okta-app",
            title: "Create the app in Okta",
            summary: oktaSetup ? null : "The app name and key URL appear here after you continue.",
            complete: connected || !!oktaClientId.trim(),
            body: oktaSetup && !connected ? (
                <OktaAppGuide orgDomain={oktaSetup.orgDomain} bindingNonce={oktaSetup.bindingNonce} jwksUrl={oktaSetup.jwksUrl} />
            ) : null,
        },
        {
            key: "okta-client",
            title: "Paste the app's Client ID",
            summary: connected ? "Connected." : null,
            complete: connected,
            body: oktaSetup && !connected ? (
                <OktaClientIdForm
                    integrationId={oktaSetup.id}
                    displayName={displayName}
                    orgDomain={oktaSetup.orgDomain}
                    clientId={oktaClientId}
                    onClientIdChange={setOktaClientId}
                    onConnected={() => finishConnect(oktaSetup.id, provider)}
                />
            ) : null,
        },
    ];

    let pathSteps = [];
    if (isEdit && oktaSetup) {
        pathSteps = [
            {
                key: "okta-domain",
                title: "Enter your Okta domain",
                summary: oktaHost(oktaSetup.orgDomain),
                complete: true,
            },
            ...oktaAppSteps,
            scopeStep,
        ];
    } else if (isEdit) {
        const status = existing?.status;
        const failing = status === "at_risk" || status === "error";
        pathSteps = [
            {
                key: "details",
                title: "Connection details",
                state: status === "pending" ? "current" : failing ? "failed" : "done",
                summary:
                    status === "at_risk"
                        ? EDIT_AT_RISK[provider] ?? "Credentials are expiring or being rejected. Update them below."
                        : status === "error"
                          ? existing?.lastSyncError || "The last sync failed. History on the card has the details."
                          : status === "pending"
                            ? "Not connected yet."
                            : null,
                body: detailError ? (
                    <ErrorText>{`${detailError} Go back and open it again.`}</ErrorText>
                ) : !detailLoaded ? (
                    <p aria-busy="true" className={`${NOTE_CLASS} m-0`}>Loading…</p>
                ) : (
                    <Panel className="grid gap-[12px] p-[14px]">
                        {nameField}
                        {detailInputs}
                        <label className="flex cursor-pointer items-start gap-[8px]">
                            <input
                                type="checkbox"
                                checked={isPrimary}
                                onChange={(e) => setIsPrimary(e.target.checked)}
                                className="mt-[2px]"
                            />
                            <span className="grid gap-[2px]">
                                <span className="text-[12px] text-[color:var(--ink)]">Primary directory</span>
                                <span className={`${NOTE_CLASS} leading-[1.4]`}>
                                    When one person is in two connected directories, Origin uses their account from this one. Only one directory can be primary.
                                </span>
                            </span>
                        </label>
                        {managedEntra ? (
                            <p className={`${NOTE_CLASS} m-0`}>Origin manages this app and its credentials. Re-authorize if Microsoft asks for consent again.</p>
                        ) : null}
                        {oktaServiceApp ? (
                            <p className={`${NOTE_CLASS} m-0`}>No secret is stored here. Origin signs in with its own key.</p>
                        ) : null}
                        {managedEntra ? <ManagedMicrosoftPermissionsPanel /> : null}
                        {isOauth && !managed ? <EntraPermissionsPanel /> : null}
                        {provider === "GOOGLE" ? <GooglePermissionsPanel /> : null}
                        {oktaServiceApp ? <OktaPermissionsPanel /> : null}
                        {submitRow}
                    </Panel>
                ),
            },
        ];
        if (canWriteForm && canToggleSync(status)) {
            pathSteps.push({
                key: "syncing",
                title: "Syncing",
                state: status === "disabled" ? "current" : "done",
                body: (
                    <SyncSwitch
                        integrationId={integrationId}
                        status={status}
                        lastAuthorizingCapture={lastAuthorizingCapture}
                        onChanged={() => onSavedRef.current?.()}
                    />
                ),
            });
        }
    } else if (copying) {
        pathSteps = [
            {
                key: "copy",
                title: "Reuse the Copilot connection",
                summary: connected ? "Connected." : null,
                complete: connected,
                body: connected ? null : (
                    <Panel className="grid gap-[12px] p-[14px]">
                        {nameField}
                        <CopiedMicrosoftAppNote
                            target={COPY_TARGET.DIRECTORY}
                            managed={copyFromCopilot.managed}
                            source={
                                copyFromCopilot.entraTenantId ? (
                                    <>
                                        your Microsoft 365 Copilot connection for tenant{" "}
                                        <span className="font-mono">{copyFromCopilot.entraTenantId}</span>
                                    </>
                                ) : (
                                    "your Microsoft 365 Copilot connection"
                                )
                            }
                            className="m-0 text-[12px] leading-[1.45] text-[color:var(--muted)]"
                        />
                        {submitRow}
                    </Panel>
                ),
            },
            scopeStep,
        ];
    } else if (managedEntra) {
        pathSteps = [
            {
                key: "consent",
                title: "Grant admin consent in Microsoft",
                summary: connected ? "Admin consent granted." : null,
                complete: connected,
                body: connected ? null : (
                    <Panel className="grid gap-[12px] p-[14px]">
                        {nameField}
                        <p className={`${NOTE_CLASS} m-0`}>A Microsoft admin approves Origin&apos;s app in a new tab. There is nothing to create or rotate.</p>
                        <ManagedMicrosoftPermissionsPanel />
                        {submitRow}
                    </Panel>
                ),
            },
            scopeStep,
        ];
    } else if (provider === "ENTRA_ID") {
        pathSteps = [
            {
                key: "register",
                title: "Register an app in Microsoft Entra",
                complete: connected || typed,
                body: connected ? null : <EntraAppGuide redirectUri={directoryOauthRedirectUri(window.location.origin)} />,
            },
            {
                key: "details",
                title: "Enter the app registration",
                summary: connected ? "Connected." : null,
                complete: connected,
                body: connected ? null : (
                    <Panel className="grid gap-[12px] p-[14px]">
                        {nameField}
                        {detailInputs}
                        {submitRow}
                    </Panel>
                ),
            },
            scopeStep,
        ];
    } else if (oktaServiceApp) {
        pathSteps = [
            {
                key: "okta-domain",
                title: "Enter your Okta domain",
                summary: oktaSetup ? oktaHost(oktaSetup.orgDomain) : null,
                complete: !!oktaSetup,
                body: oktaSetup ? null : (
                    <Panel className="grid gap-[12px] p-[14px]">
                        {nameField}
                        {detailInputs}
                        <p className={`${NOTE_CLASS} m-0`}>Origin signs in to Okta with its own key, so no secret is stored here.</p>
                        {submitRow}
                    </Panel>
                ),
            },
            ...oktaAppSteps,
            scopeStep,
        ];
    } else if (provider === "OKTA") {
        pathSteps = [
            {
                key: "token",
                title: "Create an API token in Okta",
                complete: connected || typed,
                body: connected ? null : <OktaTokenGuide orgDomain={config.org_domain} />,
            },
            {
                key: "details",
                title: "Enter the connection details",
                summary: connected ? "Connected." : null,
                complete: connected,
                body: connected ? null : (
                    <Panel className="grid gap-[12px] p-[14px]">
                        {nameField}
                        {detailInputs}
                        {submitRow}
                    </Panel>
                ),
            },
            scopeStep,
        ];
    } else if (provider === "GOOGLE") {
        pathSteps = [
            {
                key: "cloud",
                title: "Create a service account in Google Cloud",
                complete: connected || typed,
                body: connected ? null : <GoogleCloudGuide />,
            },
            {
                key: "delegation",
                title: "Allow domain-wide delegation",
                complete: connected || typed,
                body: connected ? null : <GoogleDelegationGuide />,
            },
            {
                key: "details",
                title: "Enter the connection details",
                summary: connected ? "Connected." : null,
                complete: connected,
                body: connected ? null : (
                    <Panel className="grid gap-[12px] p-[14px]">
                        {nameField}
                        {detailInputs}
                        {submitRow}
                    </Panel>
                ),
            },
            scopeStep,
        ];
    } else {
        pathSteps = [
            { key: "connect", title: "Connect", summary: "Starts after you choose a directory.", complete: false },
            { ...scopeStep, summary: "Available once the connection is made." },
        ];
    }

    const allSteps = isEdit
        ? pathSteps
        : [
              {
                  key: "choose",
                  title: "Choose a directory",
                  summary: locked ? selected?.title : null,
                  complete: !!selected,
                  body: locked ? null : (
                      <div className="grid gap-[8px]">
                          <Panel className="grid gap-0">
                              {[...choices, ...(manualOpen ? MANUAL_CHOICES : [])].map((choice) => (
                                  <MethodOption
                                      key={choice.key}
                                      name="directory-connect-method"
                                      selected={choice.key === selectedKey}
                                      title={choice.title}
                                      description={choice.description}
                                      disabledReason={choice.disabledReason ?? null}
                                      onSelect={() => choose(choice)}
                                  />
                              ))}
                          </Panel>
                          {!manualSelected ? (
                              <button
                                  type="button"
                                  onClick={() => setShowManual((v) => !v)}
                                  className={`${linkCls} justify-self-start`}
                              >
                                  {manualOpen ? "Hide other ways to connect" : "Other ways to connect"}
                              </button>
                          ) : null}
                      </div>
                  ),
              },
              ...pathSteps,
          ];

    let currentFound = false;
    const resolved = allSteps.map((step) => {
        if (step.state) return step;
        if (step.complete) return { ...step, state: "done" };
        if (!currentFound) {
            currentFound = true;
            return { ...step, state: "current" };
        }
        return { ...step, state: "todo" };
    });

    const pill = isEdit ? statusPill(existing?.status) : null;
    const source = isEdit
        ? {
              name: existing?.displayName || PROVIDER_LABEL[provider] || "Directory",
              blurb: integrationDescription({ provider, managed }),
              logo: PROVIDER_LOGO[provider],
          }
        : {
              name: "Connect a directory",
              blurb: "Origin reads people, groups and devices from your identity provider. It never changes anything there.",
              logo: PROVIDER_LOGO[provider] ?? null,
          };
    const storageNote = SETUP_ASIDE[selectedKey];

    return (
        <SourcePage
            source={source}
            health={pill}
            onBack={onBack}
            backLabel="Back to connections"
            busy={isEdit && !detailLoaded && !detailError}
            aside={
                <>
                    <AsideSection title="What Origin reads">
                        <p className="m-0">Users, groups, devices and admin roles. Access is read-only.</p>
                    </AsideSection>
                    {storageNote ? (
                        <AsideSection title={storageNote.title}>
                            <p className="m-0">{storageNote.text}</p>
                        </AsideSection>
                    ) : null}
                </>
            }
        >
            <Steps label={isEdit ? `${source.name} settings` : "Connect a directory"}>
                {resolved.map((step, index) => (
                    <Step
                        key={step.key}
                        number={index + 1}
                        state={step.state}
                        title={step.title}
                        summary={step.summary}
                        action={step.action}
                        last={index === resolved.length - 1}
                    >
                        {step.body ?? null}
                    </Step>
                ))}
            </Steps>
        </SourcePage>
    );
}

// ── Tab ──────────────────────────────────────────────────────────

// The identity REST calls go through restGet/restPost, which throw a
// plain Error whose message is `GET /path 403: {body}` — there is no
// `.status` field. A 403 here means the role can read the Directory page
// (it holds directory_data.read for the browser) but not integrations —
// an honest empty state, NOT a red error banner. Detect it by parsing the
// `<status>: ` delimiter the rest client embeds.
function isForbiddenError(err) {
    return /\s403:\s/.test(String(err?.message ?? err ?? ""));
}

export default function IntegrationsTab() {
    const caps = useMyPermissions();
    // integrations.read is required to load the table at all; write/manage/
    // sync gate the mutating affordances. hasPermission fails open while
    // caps load / on a pre-RBAC tenant, so the page behaves as before until
    // a concrete set says otherwise.
    const canRead = hasPermission(caps, PERMISSIONS.INTEGRATIONS_READ);
    const canWrite = hasPermission(caps, PERMISSIONS.INTEGRATIONS_WRITE);
    const canManage = hasPermission(caps, PERMISSIONS.INTEGRATIONS_MANAGE);
    const canSync = hasPermission(caps, PERMISSIONS.INTEGRATIONS_EXECUTE_SYNC);

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [forbidden, setForbidden] = useState(false);
    const [overlay, setOverlay] = useState(null);
    const [page, setPage] = useState(null);
    const [syncState, setSyncState] = useState({});
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [actionError, setActionError] = useState(null);

    const refetch = useCallback(async () => {
        // Don't even fire the list call if the role can't read integrations
        // — render the empty/forbidden state directly. (Defaults open while
        // caps load, so this only short-circuits a definitively-denied role.)
        if (!canRead) { setForbidden(true); setRows([]); return null; }
        setLoading(true);
        setError(null);
        try {
            const res = await listDirectoryIntegrations();
            const list = (res?.integrations ?? []).map(flattenIntegration);
            setRows(list);
            setForbidden(false);
            // Returned as well as stored: `rows` isn't readable in the same tick.
            return list;
        } catch (err) {
            console.error("[directory integrations]", err);
            // A 403 is a permission boundary, not a failure to surface as a
            // red banner — show the neutral empty state instead.
            if (isForbiddenError(err)) { setForbidden(true); setRows([]); }
            else setError(humanError(err, "Failed to load integrations"));
            return null;
        } finally {
            setLoading(false);
        }
    }, [canRead]);

    useEffect(() => { refetch().catch(() => {}); }, [refetch]);

    useEffect(() => {
        if (!actionError) return;
        const t = setTimeout(() => setActionError(null), 5000);
        return () => clearTimeout(t);
    }, [actionError]);

    // Poll while any row is mid-flight (`syncing` or `deleting`) so the table
    // reflects completion without a manual refresh: a sync finishing reverts to
    // its stored status, and a `deleting` row drops out once the directory-sync
    // finalizer removes it server-side. Driven by the server-derived status rather
    // than a client timer, so it resumes after a page refresh and self-stops when
    // nothing is in flight.
    const anyInFlight = rows.some((r) => r.status === "syncing" || r.status === "deleting");
    useEffect(() => {
        if (!anyInFlight) return;
        let cancelled = false;
        const tick = async () => {
            try {
                const res = await listDirectoryIntegrations();
                if (cancelled) return;
                const list = (res?.integrations ?? []).map(flattenIntegration);
                setRows(list);
                // Drop the optimistic "pending" flag once the run is no longer
                // in flight server-side.
                setSyncState((prev) => {
                    let changed = false;
                    const next = { ...prev };
                    for (const id of Object.keys(prev)) {
                        if (prev[id] !== "pending") continue;
                        const cur = list.find((i) => i.id === id);
                        if (!cur || cur.status !== "syncing") { delete next[id]; changed = true; }
                    }
                    return changed ? next : prev;
                });
            } catch {}
        };
        // Escalating rather than flat — see `syncPollDelayMs`. Re-mounts at the
        // fast tier whenever the in-flight set changes, so a new sync is snappy.
        let elapsed = 0;
        let handle = null;
        const schedule = () => {
            const delay = syncPollDelayMs(elapsed);
            handle = window.setTimeout(async () => {
                elapsed += delay;
                await tick();
                if (!cancelled) schedule();
            }, delay);
        };
        schedule();
        return () => { cancelled = true; if (handle) window.clearTimeout(handle); };
    }, [anyInFlight]);

    const handleSync = useCallback(async (id) => {
        setSyncState((m) => ({ ...m, [id]: "pending" }));
        try {
            // Pick the type the backend's dispatch_mode gate will accept — read
            // fresh so a stale row can't send a type the gate 412s.
            const detail = await getDirectoryIntegration(id);
            const syncType = resolveSyncType(detail?.config);
            if (!syncType) {
                setSyncState((m) => ({
                    ...m,
                    [id]: "No sync scope is configured. Open Scope and sync the whole tenant or select groups.",
                }));
                return;
            }
            await triggerDirectorySync(id, syncType);
            // Optimistically flip to syncing so the row locks immediately and
            // the poll engages; the next poll re-derives it from the server.
            setRows((prev) => prev.map((r) => (r.id === id ? { ...r, status: "syncing" } : r)));
        } catch (err) {
            setSyncState((m) => ({ ...m, [id]: humanError(err, "Sync failed to start") }));
        }
    }, []);

    const handleDelete = useCallback(async (id) => {
        setConfirmDelete(null);
        // Delete is server-authoritative: the RPC marks the row `deleting` and a
        // finalizer removes it within ~1 poll. Reflect that by refetching — the
        // row shows `deleting` (the poll keeps refetching until it's gone) rather
        // than optimistically dropping it, which would misrepresent a row that
        // still exists until the finalizer runs.
        try {
            await deleteDirectoryIntegration(id);
            refetch().catch(() => {});
        } catch (err) {
            setActionError(`Delete failed: ${humanError(err, "unknown error")}`);
        }
    }, [refetch]);

    const afterScopeSaved = useCallback(async (id, res) => {
        const list = await refetch().catch(() => null);
        if (res?.scopeChanged && list && !list.some((r) => r.id === id && r.status === "syncing")) {
            setSyncState((m) => ({ ...m, [id]: SCOPE_RESYNC_NOT_QUEUED }));
        }
    }, [refetch]);

    const closePage = useCallback(() => {
        setPage(null);
        refetch().catch(() => {});
    }, [refetch]);

    const editingId = page?.mode === "edit" ? page.id : null;
    const editingExisting = editingId ? rows.find((r) => r.id === editingId) ?? null : null;
    const lastAuthorizingCapture =
        !!editingExisting
        && authorizesCapture(editingExisting)
        && rows.filter(authorizesCapture).length === 1;

    if (page) {
        return (
            <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto py-[16px]">
                <IntegrationForm
                    key={page.mode === "edit" ? page.id : "new"}
                    integrationId={editingId}
                    existing={page.mode === "edit" ? editingExisting : null}
                    directories={rows}
                    lastAuthorizingCapture={lastAuthorizingCapture}
                    onBack={closePage}
                    onSaved={(result) => {
                        if (result?.id) refetch().catch(() => {});
                        else closePage();
                    }}
                    onScopeSaved={({ id, ...res }) => {
                        setPage(null);
                        afterScopeSaved(id, res);
                    }}
                />
            </div>
        );
    }

    const empty = !loading && !error && !forbidden && rows.length === 0;

    return (
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto py-[8px]">
            <div className="grid shrink-0 gap-[10px] pb-[12px]">
                <div className="flex flex-wrap items-center justify-between gap-x-[10px] gap-y-[6px] border-y border-[color:var(--line-soft)] py-[6px] text-[11px] leading-[1.18] text-[color:var(--muted)]">
                    <div className="flex min-w-0 flex-wrap items-center gap-x-[10px] gap-y-[3px]">
                        <span>
                            <strong className="text-[color:var(--ink)]">{formatCompact(rows.length)}</strong>{" "}
                            {rows.length === 1 ? "integration" : "integrations"}
                        </span>
                        {actionError ? <span className="text-[color:var(--severity-high)]">{actionError}</span> : null}
                    </div>
                    {canWrite && !forbidden ? (
                        <button
                            type="button"
                            onClick={() => setPage({ mode: "new" })}
                            className={empty ? primaryBtnCls : secondaryBtnCls}
                        >
                            Add integration
                        </button>
                    ) : null}
                </div>
                {error ? (
                    <div
                        role="alert"
                        className="flex flex-wrap items-center justify-between gap-[10px] border border-[color:var(--severity-high)] bg-[color:color-mix(in_srgb,var(--severity-high)_7%,transparent)] px-[14px] py-[10px] text-[13px] text-[color:var(--ink)]"
                    >
                        <span className="min-w-0 [overflow-wrap:anywhere]">Couldn&apos;t load integrations: {error}</span>
                        <button type="button" onClick={() => refetch().catch(() => {})} className={secondaryBtnCls}>
                            Retry
                        </button>
                    </div>
                ) : null}
            </div>

            {forbidden ? (
                <div className="grid gap-[4px] border border-[color:var(--line)] bg-[color:var(--panel)] p-[16px]">
                    <strong className="text-[13px] font-medium text-[color:var(--ink)]">
                        Integrations aren’t available for your role
                    </strong>
                    <span className={NOTE_CLASS}>You can browse synced users, groups and devices from the tabs above.</span>
                </div>
            ) : loading && rows.length === 0 ? (
                <div aria-busy="true" className="grid gap-[14px]">
                    {[0, 1].map((i) => (
                        <div key={i} className="grid gap-[14px] border border-[color:var(--line)] bg-[color:var(--panel)] p-[16px]">
                            <div className="flex items-center gap-[12px]">
                                <span className="h-[28px] w-[28px] bg-[color:var(--gray-fill)]" />
                                <span className="h-[12px] w-[180px] max-w-full bg-[color:var(--gray-fill)]" />
                            </div>
                            <span className="h-[10px] w-[60%] bg-[color:var(--gray-fill)]" />
                        </div>
                    ))}
                </div>
            ) : empty ? (
                <div className="flex flex-col gap-[14px] border border-dashed border-[color:var(--line-strong)] p-[16px]">
                    <div className="flex items-start gap-[12px] max-[560px]:flex-wrap">
                        <div className="grid min-w-0 flex-1 gap-[2px]">
                            <h3 className="m-0 text-[14px] font-medium leading-[1.2] text-[color:var(--ink)]">No directory connected</h3>
                            <span className={NOTE_CLASS}>
                                Connect Microsoft Entra ID, Okta, or Google Workspace so Origin can attribute activity to people and groups.
                            </span>
                        </div>
                        <StatusPill tone={TONE.SETUP} label="Not set up" />
                    </div>
                </div>
            ) : (
                <div aria-busy={loading ? "true" : undefined} className="grid gap-[14px]">
                    {rows.map((r) => (
                        <IntegrationCard
                            key={r.id}
                            row={r}
                            syncState={syncState[r.id]}
                            inDeleteConfirm={confirmDelete === r.id}
                            onShowHistory={(id) => setOverlay({ mode: "history", id })}
                            onShowScope={(target) => setOverlay({ mode: "scope", id: target.id, provider: target.provider })}
                            onSync={handleSync}
                            onEdit={(id) => { setConfirmDelete(null); setPage({ mode: "edit", id }); }}
                            onDeleteRequest={(id) => setConfirmDelete(id)}
                            onDeleteConfirm={handleDelete}
                            onCancelConfirm={() => setConfirmDelete(null)}
                            canWrite={canWrite}
                            canManage={canManage}
                            canSync={canSync}
                        />
                    ))}
                </div>
            )}

            <OverlayDialog
                open={overlay?.mode === "history"}
                onClose={() => setOverlay(null)}
                analyticsId={DIALOG.INTEGRATION_SYNC_HISTORY}
                eyebrow="Identity"
                title="Sync history"
                width="720px"
            >
                {overlay?.mode === "history" ? (
                    <SyncHistoryView integrationId={overlay.id} />
                ) : null}
            </OverlayDialog>
            <OverlayDialog
                open={overlay?.mode === "scope"}
                onClose={() => setOverlay(null)}
                analyticsId={DIALOG.INTEGRATION_SYNC_SCOPE}
                eyebrow="Identity"
                title="Sync scope"
                width="560px"
            >
                {overlay?.mode === "scope" ? (
                    <GroupScopeView
                        integrationId={overlay.id}
                        provider={overlay.provider}
                        onSaved={(res) => {
                            const id = overlay.id;
                            setOverlay(null);
                            afterScopeSaved(id, res);
                        }}
                    />
                ) : null}
            </OverlayDialog>
        </div>
    );
}
